class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.isAdjusting = false;
    this.normalizeValue = value => (value ?? '').toString().trim();
    this.didChangeSinceClick = false;
    this.clickFallbackTimeoutId = null;
    this.renderRequestId = 0;
    this.renderAbortController = null;
    this.dropdownWrappers = [];
    this.dropdownListeners = new Map();
    this.onWindowClickCloseDropdownBound = null;
    this.isCustomProduct =
      this.dataset.mobileVariantStepper === 'true' ||
      this.dataset.templateSuffix === 'custom-product' ||
      (this.dataset.template || '').includes('custom-product');
    this.requireExplicitVariantSelection = this.isCustomProduct;
    this._dbgSend = () => {};
    this.debugLog = () => {};
    this.debugMediaSnapshot = () => {};
    this._carouselDebugState = {};
    this._stylePreviewColor = null;
    this.initCarouselFirstStyleDebug();
    this._strapScrollRequestId = 0;
    this._strapScrollPending = false;
    this._preserveActiveMediaOnNextUpdate = false;
    this._preserveActiveMediaId = null;
    this._onAnyMediaInteractBound = null;
    if (this.isCustomProduct) {
      this._onAnyMediaInteractBound = e => {
        if (!this._strapScrollPending) return;
        const hit = e.target?.closest?.(
          '.product__media .swiper-button--prev, .product__media .swiper-button--next, .product__media button[aria-label="Previous slide"], .product__media button[aria-label="Next slide"], .thumbnail-carousel'
        );
        if (!hit) return;
        this._strapScrollRequestId += 1;
        this._strapScrollPending = false;
        // #region debug-point C:strap-autoscroll-cancel
        window.__traeDbgPdpBlipClicks?.('C', 'strapAutoScroll:cancel', {
          reason: hit.className || hit.getAttribute?.('aria-label') || null
        });
        // #endregion
      };
      document.addEventListener('click', this._onAnyMediaInteractBound, true);
    }

    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    this.purchaseOptions = document.querySelector(
      `#Product-Purchase-Options-${this.dataset.sectionId}`
    );
    this.productBar = document.querySelector(
      `#ProductBar-${this.dataset.sectionId}`
    );
    const installmentsSelector = (this.dataset.installmentsFormId || '').trim();
    this.installmentsForm = installmentsSelector
      ? document.querySelector(
          installmentsSelector.startsWith('#')
            ? installmentsSelector
            : `#${installmentsSelector}`
        )
      : null;

    this.onVariantChangeBound = this.onVariantChange.bind(this);
    this.addEventListener('change', this.onVariantChangeBound);
    this.onClickFallbackBound = this.onClickFallback.bind(this);
    this.addEventListener('click', this.onClickFallbackBound);
    this.onOptionItemClickBound = this.onOptionItemClick.bind(this);
    this.addEventListener('click', this.onOptionItemClickBound);

    this.reportInitialCarouselDebug('constructor:boot', {
      requireExplicitVariantSelection: this.requireExplicitVariantSelection,
      hasAnyVariantOptionSelected: this.hasAnyVariantOptionSelected(),
      currentVariantId: this.currentVariant?.id || null
    });

    if (this.requireExplicitVariantSelection) {
      this.clearInitialVariantOptionSelections();
      requestAnimationFrame(() => {
        this.clearInitialVariantOptionSelections();
        this.reportInitialCarouselDebug('constructor:after-raf-clear', {
          requireExplicitVariantSelection: this.requireExplicitVariantSelection,
          hasAnyVariantOptionSelected: this.hasAnyVariantOptionSelected(),
          currentVariantId: this.currentVariant?.id || null
        });
        this.updateOptions();
        this.updateVariant();
        this.initUpdateMedia();
        this.syncAddButtonState();
        this.updateVariantTabsInteractivity();
      });
    }

    this.updateOptions();
    this.updateVariantStatuses();
    if (this.requireExplicitVariantSelection) {
      this.currentVariant = null;
    } else {
      this.updateVariant();
      if (this.currentVariant && !this.currentVariant.available) {
        const firstAvailable = this.variants.find(v => v.available);
        if (firstAvailable) {
          this.isAdjusting = true;
          this.applyVariantSelection(firstAvailable);
          this.isAdjusting = false;
          this.updateVariant();
        }
      }
      this.initUpdateMedia();
      // Safety net for is-color-ready (see updateMedia()): if some
      // edge case this doesn't already cover leaves it unset, the
      // gallery would stay invisible forever (CSS in
      // section-product.css hides it until that class appears) --
      // far worse than the flash this was meant to fix. Force it on
      // after a generous timeout no matter what.
      setTimeout(() => {
        const root = (this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document)
          .querySelector('product-media');
        root?.classList.add('is-color-ready');
      }, 4000);
    }

    if (this.form) {
      if (this.variants.some(v => v.available)) {
        this.form.toggleAddButton(!this.currentVariant?.available, '');
      } else {
        this.form.toggleAddButton(true, window.theme?.strings?.soldOut || 'Sold out');
      }
    }
    this.createDropdownFromButtons();

    this.enableDropdownStyle();
    this.ensureWellnessThumbDelegation();
    this.initMobileVariantStepper();
    this.initVariantTabsBehavior();
    this.initCustomStrap();
    this.initCarouselDragScroll();

    // Cart add/remove never used to reach the PDP at all -- swatches and
    // the Add to Cart button only reflected whatever `this.variants`
    // (product.variants JSON) said at page load, so removing a sold-out
    // item from the cart drawer never re-enabled it here. Re-fetching
    // /products/{handle}.js (real, server-computed availability, not a
    // guess based on the cart action) keeps this safe against overselling
    // -- it only re-enables Add to Cart when Shopify itself reports stock.
    this._cartUpdatedListener = event => this.refreshAvailabilityFromServer(event);
    document.addEventListener('cart:updated', this._cartUpdatedListener);

    // Populated by refreshCartVariantIds() -- checked synchronously by
    // syncAddButtonState() (a variant id present here means "already in
    // the cart, don't let it be added again until removed"). Starts
    // empty and gets filled in below so this also holds correctly on a
    // plain page refresh, not just right after clicking Add to Cart.
    this._cartVariantIds = new Set();
    this.refreshCartVariantIds();
  }

  refreshCartVariantIds() {
    return fetch('/cart.js')
      .then(response => (response.ok ? response.json() : null))
      .then(cart => {
        if (!cart || !Array.isArray(cart.items)) return;
        this._cartVariantIds = new Set(cart.items.map(item => item.variant_id));
        this.syncAddButtonState();
      })
      .catch(() => {});
  }

  refreshAvailabilityFromServer(event) {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    const isFromRemoval = Boolean(event?.detail?.removed);
    const cartIdsRefreshed = this.refreshCartVariantIds();

    const requestId = (this._availabilityRequestId = (this._availabilityRequestId || 0) + 1);

    return fetch(`/products/${handle}.js`)
      .then(response => (response.ok ? response.json() : null))
      .then(async freshProduct => {
        if (requestId !== this._availabilityRequestId) return; // superseded by a newer refresh
        if (!freshProduct || !Array.isArray(freshProduct.variants)) return;

        const freshById = new Map(freshProduct.variants.map(v => [v.id, v]));
        this.variants = this.variants.map(variant => {
          const fresh = freshById.get(variant.id);
          if (!fresh) return variant;
          return {
            ...variant,
            available: fresh.available,
            inventory_quantity: fresh.inventory_quantity,
            inventory_management: fresh.inventory_management,
            inventory_policy: fresh.inventory_policy
          };
        });

        if (!this.hasAnyVariantOptionSelected()) {
          // Nothing is selected right now -- most commonly because
          // product-form.js's resetProductSelectorToInitialState() just
          // cleared every option after a successful add-to-cart (it runs
          // unconditionally, for every product, right before dispatching
          // this same cart:updated event).
          if (isFromRemoval && this._lastKnownVariantId) {
            // The shopper just removed this item from the cart drawer --
            // restore the selection it had before the post-add reset
            // cleared it, so Add to Cart is immediately ready again
            // instead of making them re-pick color/size from scratch.
            // Fresh availability was already merged into this.variants
            // above, so if that combination genuinely isn't in stock any
            // more, the normal disabled/"Sold out" handling below still
            // applies correctly -- this only restores the SELECTION, not
            // a false "available" assumption.
            const lastVariant = this.variants.find(v => v.id === this._lastKnownVariantId);
            if (lastVariant) {
              this.applyVariantSelection(lastVariant);
            }
          }

          if (!this.hasAnyVariantOptionSelected()) {
            // Calling updateVariantStatuses() here would run its
            // ensureValidSelections() fallback, which auto-picks the
            // FIRST enabled option per wrapper when nothing is checked --
            // silently selecting a variant (and showing it as sold out if
            // that default combination isn't in stock) instead of leaving
            // the intentional "nothing selected" state alone. this.variants
            // is already updated above with fresh data, so swatches will
            // reflect it correctly once the shopper picks again.
            return;
          }
        }

        this.updateVariantStatuses();

        this.currentVariant = this.getVariantData();

        // Wait for the real /cart.js snapshot too before deciding the
        // button's state -- syncAddButtonState() checks this._cartVariantIds
        // synchronously, so without this it could run against a stale
        // (pre-removal) cart snapshot and briefly show the wrong state.
        await cartIdsRefreshed;
        this.syncAddButtonState();

        if (this.currentVariant) {
          this.updateQuantityInput(this.currentVariant.inventory_quantity || 0);
        }
      })
      .catch(() => {});
  }

  clearInitialVariantOptionSelections() {
    this.querySelectorAll(
      'fieldset.product-options[data-input-wrapper]:not([data-custom-strap-wrapper]):not([data-custom-bottoms-wrapper]) input[type="radio"]'
    ).forEach(input => {
      input.checked = false;
      input.removeAttribute('checked');
    });
    this.querySelectorAll('input[name^="options["]').forEach(input => {
      if (input.type === 'radio' || input.type === 'checkbox') {
        input.checked = false;
      }
      input.removeAttribute('checked');
    });
    this.querySelectorAll('select[name^="options["]').forEach(select => {
      const hasEmpty = [...select.options].some(opt => (opt.value || '') === '');
      if (!hasEmpty) {
        const emptyOpt = document.createElement('option');
        emptyOpt.value = '';
        emptyOpt.textContent = 'Select';
        select.insertBefore(emptyOpt, select.firstChild);
      }
      Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
      select.value = '';
    });
    this.querySelectorAll('.product-options__item.is-active').forEach(el =>
      el.classList.remove('is-active')
    );
  }

  initCarouselFirstStyleDebug() {
    if (!this.isCustomProduct) return;

    this._carouselDebugSessionId = 'carousel-first-style-debug';
    this._carouselDebugServerUrl = 'http://127.0.0.1:7777/event';
    this.querySelector('[data-carousel-debug-bar]')?.remove();

    this._dbgSend = (hypothesisId, msg, data = {}) => {
      // #region debug-point A:report-event
      fetch(this._carouselDebugServerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this._carouselDebugSessionId,
          runId: 'pre-fix',
          hypothesisId,
          location: 'assets/product-selector.js',
          msg,
          data,
          ts: Date.now()
        })
      }).catch(() => {});
      // #endregion
    };

    this.debugLog = (message, data = {}) => {
      this._dbgSend('Z', `[DEBUG] ${message}`, data);
    };

    this.debugMediaSnapshot = label => {
      const sectionElement =
        this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
      const productMediaRoot = sectionElement.querySelector('product-media');
      const slides = productMediaRoot
        ? [...productMediaRoot.querySelectorAll('.swiper-wrapper .swiper-slide[data-media-id]')]
        : [];
      const activeSlide =
        productMediaRoot?.querySelector('.swiper-slide-active[data-media-id]') ||
        slides[0] ||
        null;
      const payload = {
        label,
        currentVariantId: this.currentVariant?.id || null,
        selectedStyle: this.getSelectedStyleValue() || null,
        selectedColor: this.getSelectedColorValue() || null,
        activeMediaId: Number(activeSlide?.getAttribute('data-media-id')) || null,
        visibleSlideIds: slides
          .filter(slide => slide.offsetParent !== null)
          .map(slide => Number(slide.getAttribute('data-media-id')) || null)
          .filter(id => Number.isFinite(id))
          .slice(0, 12)
      };
      this.updateCarouselDebugBar({ snapshot: payload });
      this._dbgSend('E', `[DEBUG] media snapshot ${label}`, payload);
    };
  }

  ensureCarouselDebugBar() {
    return null;
  }

  updateCarouselDebugBar(patch = {}) {
    this._carouselDebugState = {
      ...this._carouselDebugState,
      ...patch,
      updatedAt: new Date().toLocaleTimeString()
    };
  }

  collectCarouselSlidePreview(limit = 8) {
    const sectionElement =
      this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
    const productMediaRoot = sectionElement.querySelector('product-media');
    const slides = productMediaRoot
      ? [...productMediaRoot.querySelectorAll('.swiper-wrapper .swiper-slide[data-media-id]')]
      : [];

    return slides.slice(0, limit).map(slide => ({
      mediaId: Number(slide.getAttribute('data-media-id')) || null,
      style: this.normalizeValue(slide.getAttribute('data-style')).toLowerCase() || null,
      color: this.normalizeValue(slide.getAttribute('data-color')).toLowerCase() || null
    }));
  }

  reportInitialCarouselDebug(source, payload = {}) {
    const merged = {
      source,
      ...payload,
      slidePreview: this.collectCarouselSlidePreview()
    };
    this.updateCarouselDebugBar(merged);
    this._dbgSend('A', `[DEBUG] ${source}`, merged);
  }

  getVariantTabHeaders() {
    const container = this.querySelector('.variant-tabs__headers');
    if (!container) return [];
    return [...container.querySelectorAll('.variant-tabs__header')];
  }

  getVariantTabPanels() {
    return [
      ...this.querySelectorAll('[data-variant-tab]:not(.variant-tabs__header)')
    ];
  }

  hasSelectedAllVariantOptions() {
    const wrappers = this.getVariantInputWrappers();
    if (!wrappers.length) return true;
    return wrappers.every(wrapper => {
      const checked = wrapper.querySelector(
        'input[name^="options["][type="radio"]:checked:not(:disabled)'
      );
      if (checked) return true;
      const select = wrapper.querySelector('select[name^="options["]');
      if (!select) return false;
      return Boolean((select.value || '').trim());
    });
  }

  getCoreVariantInputWrappers() {
    return this.getVariantInputWrappers().filter(
      wrapper =>
        !wrapper.hasAttribute('data-custom-strap-wrapper') &&
        !wrapper.hasAttribute('data-custom-bottoms-wrapper')
    );
  }

  hasSelectedAllCoreVariantOptions() {
    const wrappers = this.getCoreVariantInputWrappers();
    if (!wrappers.length) return true;
    return wrappers.every(wrapper => {
      const checked = wrapper.querySelector(
        'input[name^="options["][type="radio"]:checked:not(:disabled)'
      );
      if (checked) return true;
      const select = wrapper.querySelector('select[name^="options["]');
      if (!select) return false;
      return Boolean((select.value || '').trim());
    });
  }

  hasAnyVariantOptionSelected() {
    const wrappers = this.getVariantInputWrappers();
    if (!wrappers.length) return false;
    return wrappers.some(wrapper => {
      const checked = wrapper.querySelector(
        'input[name^="options["][type="radio"]:checked:not(:disabled)'
      );
      if (checked) return true;
      const select = wrapper.querySelector('select[name^="options["]');
      if (!select) return false;
      return Boolean((select.value || '').trim());
    });
  }

  initVariantTabsBehavior() {
    const tabsContainer = this.querySelector('.variant-tabs__headers');
    if (!tabsContainer) return;

    const styleFieldset = [...this.querySelectorAll('fieldset.product-options[data-input-wrapper]')].find(
      fieldset =>
        (fieldset.querySelector('.product-selector__legend-title')?.textContent || '')
          .toLowerCase()
          .includes('style')
    );
    this.variantTabsStyleFieldset = styleFieldset || null;
    if (styleFieldset && tabsContainer.parentElement) {
      tabsContainer.parentElement.insertBefore(styleFieldset, tabsContainer);
      styleFieldset.removeAttribute('data-variant-tab');
    }

    const strapHeader = tabsContainer.querySelector(
      '.variant-tabs__header[data-variant-tab="strap"]'
    );
    const sizeHeader = tabsContainer.querySelector(
      '.variant-tabs__header[data-variant-tab="size"]'
    );
    if (strapHeader && sizeHeader) {
      const pos = sizeHeader.compareDocumentPosition(strapHeader);
      const sizeBeforeStrap =
        Boolean(pos & Node.DOCUMENT_POSITION_FOLLOWING) ||
        Boolean(pos & Node.DOCUMENT_POSITION_CONTAINED_BY);
      if (sizeBeforeStrap) {
        tabsContainer.insertBefore(strapHeader, sizeHeader);
      }
    }

    this.updateVariantTabsInteractivity();
  }

  updateVariantTabsInteractivity() {
    const headers = this.getVariantTabHeaders();
    if (!headers.length) return;

    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const ready = this.requireExplicitVariantSelection
      ? (this.variantTabsStyleFieldset
        ? this.fieldsetHasSelection(this.variantTabsStyleFieldset)
        : this.hasAnyVariantOptionSelected())
      : this.hasSelectedAllVariantOptions();

    if (this.requireExplicitVariantSelection) {
      const tabGroups = headers.map(header => {
        const tabName = header.getAttribute('data-variant-tab') || '';
        const fieldsets = [
          ...this.querySelectorAll(
            `fieldset.product-options[data-variant-tab="${CSS.escape(tabName)}"]`
          )
        ];
        let selected = true;
        if (fieldsets.length) {
          if (tabName === 'strap' && this.isStrapRequired()) {
            selected = this.hasStrapSelection();
          } else if (tabName === 'bottoms') {
            selected = true;
          } else {
            selected = fieldsets.some(fs => this.fieldsetHasSelection(fs));
          }
        }
        return { header, selected };
      });

      tabGroups.forEach((group, index) => {
        if (!ready) {
          group.header.disabled = true;
          return;
        }
        const prevSelected = tabGroups
          .slice(0, index)
          .every(g => g.selected);
        group.header.disabled = !prevSelected;
      });
    } else {
      headers.forEach(h => {
        h.disabled = !ready;
      });
    }

    if (!ready) {
      headers.forEach(h => h.classList.remove('active'));
      this.getVariantTabPanels().forEach(panel => {
        panel.classList.remove('is-tab-visible');
        panel.style.display = 'none';
      });
      return;
    }

    if (!isDesktop) {
      headers.forEach(h => h.classList.remove('active'));
      this.getVariantTabPanels().forEach(panel => {
        panel.classList.remove('is-tab-visible');
        panel.style.display = '';
      });
      return;
    }

    const hasActive = headers.some(h => h.classList.contains('active') && !h.disabled);
    if (!hasActive) {
      const firstEnabled = headers.find(h => !h.disabled);
      if (firstEnabled) firstEnabled.click();
    } else if (isDesktop) {
      const activeHeader = headers.find(h => h.classList.contains('active'));
      if (activeHeader) activeHeader.disabled = false;
    }

    // #region debug-point B:tabs-state
    window.__traeDbgPdpBlipClicks?.('B', 'tabs:state', {
      ready,
      headers: headers.map(h => ({
        tab: h.getAttribute('data-variant-tab') || '',
        disabled: Boolean(h.disabled),
        active: h.classList.contains('active')
      }))
    });
    // #endregion
  }

  initCarouselDragScroll() {
    let startX, scrollLeft, dragged, activeCarousel;

    this.addEventListener('pointerdown', e => {
      const el = e.target.closest('.strap-carousel__track, .bottoms-carousel__track');
      if (!el) return;
      const carousel = el.parentElement;
      startX = e.pageX;
      scrollLeft = carousel.scrollLeft;
      dragged = false;
      activeCarousel = carousel;
      carousel.style.cursor = 'grabbing';
      if (!e.target.closest('.strap-card, .bottoms-card')) {
        e.preventDefault();
      }
    });

    this.addEventListener('pointermove', e => {
      if (!activeCarousel) return;
      if (!dragged && Math.abs(e.pageX - startX) > 4) {
        dragged = true;
      }
      activeCarousel.scrollLeft = scrollLeft - (e.pageX - startX);
    });

    const onUp = () => {
      if (!activeCarousel) return;
      activeCarousel.style.cursor = '';
      requestAnimationFrame(() => {
        activeCarousel = null;
        dragged = false;
      });
    };

    this.addEventListener('pointerup', onUp);
    this.addEventListener('pointercancel', onUp);
    this.addEventListener('pointerleave', onUp);

    this.addEventListener('click', e => {
      if (dragged && activeCarousel && activeCarousel.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }, true);
  }

  getVariantInputWrappers() {
    return [...this.querySelectorAll(this.inputWrapper)].filter(wrapper =>
      Boolean(
        wrapper.querySelector(
          'input[name^="options["], select[name^="options["]'
        )
      )
    );
  }

  getSelectedCustomStrapInputs() {
    return [...this.querySelectorAll('input[data-custom-strap-input]:checked')];
  }

  getSelectedCustomStrapProductIds() {
    return this.getSelectedCustomStrapInputs()
      .map(input => Number(input.dataset.customStrapProductId || input.value))
      .filter(id => Number.isFinite(id));
  }

  getSelectedCustomStrapMediaIds() {
    return this.getSelectedCustomStrapInputs()
      .map(input => Number(input.dataset.customStrapMediaId))
      .filter(id => Number.isFinite(id));
  }

  getSelectedCustomStrapMediaId() {
    const selectedMediaIds = this.getSelectedCustomStrapMediaIds();
    if (!selectedMediaIds.length) return null;

    if (
      Number.isFinite(this.lastSelectedCustomStrapMediaId) &&
      selectedMediaIds.includes(this.lastSelectedCustomStrapMediaId)
    ) {
      return this.lastSelectedCustomStrapMediaId;
    }

    return selectedMediaIds[0];
  }

  sortCustomStrapCards() {
    const track = this.querySelector(
      '[data-custom-strap-wrapper] .strap-carousel__track'
    );
    if (!track) return;

    const cards = [...track.querySelectorAll('label.strap-card')].filter(
      el => el.parentElement === track
    );
    if (cards.length < 2) return;

    const getTitle = el =>
      (el.querySelector('.strap-card__title')?.textContent || '').trim();

    cards
      .slice()
      .sort((a, b) =>
        getTitle(a).localeCompare(getTitle(b), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      )
      .forEach(el => track.appendChild(el));
  }

  initCustomStrap() {
    const strapInputs = [...this.querySelectorAll('input[data-custom-strap-input]')];
    if (!strapInputs.length) return;

    this.sortCustomStrapCards();

    const selectedMediaIds = this.getSelectedCustomStrapMediaIds();
    if (
      !selectedMediaIds.includes(this.lastSelectedCustomStrapMediaId) &&
      selectedMediaIds.length
    ) {
      this.lastSelectedCustomStrapMediaId = selectedMediaIds[0];
    }

    this.updateStrapLegendTitle();
  }

  hasStrapSelection() {
    const strapInputs = this.querySelectorAll('input[data-custom-strap-input]:checked:not(:disabled)');
    return strapInputs.length > 0;
  }

  getStrapSelectionCount() {
    return this.querySelectorAll('input[data-custom-strap-input]:checked:not(:disabled)').length;
  }

  isStrapRequired() {
    return this.querySelector('input[data-custom-strap-input]') !== null;
  }

  updateStrapLegendTitle() {
    const strapWrapper = this.querySelector('[data-custom-strap-wrapper]');
    if (!strapWrapper) return;

    const legendTitle = strapWrapper.querySelector('.product-selector__legend-title');
    if (!legendTitle) return;

    const count = this.getStrapSelectionCount();

    if (count === 0) {
      legendTitle.textContent = window.theme?.strings?.select_strap || 'Select Strap';
    } else if (count === 1) {
      legendTitle.textContent = window.theme?.strings?.select_2nd_strap || 'Select 2nd strap to get 1 free';
    } else if (count === 2) {
      legendTitle.textContent = window.theme?.strings?.select_3rd_free || 'Select 3rd strap - Free!';
    } else {
      legendTitle.textContent = window.theme?.strings?.straps_selected || 'Straps Selected';
    }
  }

  scrollToSelectedCustomStrapMedia({ retries = 30, requestId = null, startedAt = null } = {}) {
    if (!requestId) {
      requestId = (this._strapScrollRequestId += 1);
      this._strapScrollPending = true;
      startedAt = Date.now();
    }
    if (requestId !== this._strapScrollRequestId) return;
    if (startedAt && Date.now() - startedAt > 700) {
      this._strapScrollPending = false;
      // #region debug-point C:strap-autoscroll-timeout
      window.__traeDbgPdpBlipClicks?.('C', 'strapAutoScroll:timeout', {
        requestId
      });
      // #endregion
      return;
    }
    const mediaId = this.getSelectedCustomStrapMediaId();
    if (!mediaId) {
      this._strapScrollPending = false;
      return;
    }

    const sectionElement =
      this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
    const productMedia = sectionElement.querySelector('product-media');
    if (!productMedia) return;

    const slideExists = Boolean(
      productMedia.querySelector(`[data-media-id="${mediaId}"]`)
    );
    if (!slideExists) {
      if (retries > 0) {
        requestAnimationFrame(() =>
          this.scrollToSelectedCustomStrapMedia({
            retries: retries - 1,
            requestId,
            startedAt
          })
        );
      }
      return;
    }

    if (typeof productMedia.setActiveMedia === 'function') {
      if (requestId !== this._strapScrollRequestId || !this._strapScrollPending) {
        return;
      }
      productMedia.setActiveMedia(mediaId);
      this._strapScrollPending = false;
      // #region debug-point C:strap-autoscroll-run
      window.__traeDbgPdpBlipClicks?.('C', 'strapAutoScroll:run', {
        mediaId
      });
      // #endregion
    }
  }

  ensureWellnessThumbDelegation() {
    const sectionElement =
      this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product');
    if (!sectionElement) return;
    const wellnessThumbsWrapper = sectionElement.querySelector('.wellness-thumbs');
    if (!wellnessThumbsWrapper || wellnessThumbsWrapper.dataset.wellnessThumbDelegation) {
      return;
    }
    wellnessThumbsWrapper.dataset.wellnessThumbDelegation = '1';
    wellnessThumbsWrapper.addEventListener('click', e => {
      const thumb = e.target.closest('.thumb');
      if (!thumb || !wellnessThumbsWrapper.contains(thumb)) return;
      const img = thumb.querySelector('img');
      if (!img) return;
      const mainImg = sectionElement.querySelector('#wellness-main-image');
      if (mainImg && img.dataset.full) {
        mainImg.src = img.dataset.full;
      }
      wellnessThumbsWrapper.querySelectorAll('.thumb').forEach(t =>
        t.classList.remove('is-active')
      );
      thumb.classList.add('is-active');
    });
  }

  fieldsetHasSelection(fieldset) {
    const legendTitle = fieldset?.querySelector('.product-selector__legend-title')?.textContent?.toLowerCase() || '';
    const isStrap = legendTitle.includes('strap');

    if (isStrap) {
      return Boolean(
        fieldset?.querySelector('input[data-custom-strap-input]:checked:not(:disabled)')
      );
    }
    const checkedRadio = fieldset?.querySelector('input[type="radio"]:checked:not(:disabled)');
    if (checkedRadio) return true;
    const select = fieldset?.querySelector('select[name^="options["]');
    if (select && (select.value || '').trim()) return true;
    return Boolean(
      fieldset?.querySelector(
        'input[type="radio"]:checked:not(:disabled), input[data-custom-strap-input]:checked:not(:disabled)'
      )
    );
  }

  initMobileVariantStepper() {
    this.debugLog('stepper_init_check', {
      flag: this.dataset.mobileVariantStepper,
      width: window.innerWidth
    });
    if (this.dataset.mobileVariantStepper !== 'true') return;

    this.classList.add('product-selector--mobile-stepper-pending');
    this.mobileVariantStepperActive = false;
    this.mobileVariantStepperIndex = 0;
    this.mobileVariantStepperFieldsets = [];
    this.mobileVariantStepperNav = null;
    this.mobileVariantStepperMql = window.matchMedia('(max-width: 749px)');

    this.onMobileVariantStepperMediaChangeBound = e => {
      const matches =
        typeof e?.matches === 'boolean'
          ? e.matches
          : typeof this.mobileVariantStepperMql?.matches === 'boolean'
            ? this.mobileVariantStepperMql.matches
            : false;

      this.debugLog('stepper_media_change', {
        matches,
        width: window.innerWidth,
        fieldsets: this.querySelectorAll('fieldset.product-options[data-input-wrapper]')
          .length
      });

      if (matches) {
        this.activateMobileVariantStepper();
      } else {
        this.deactivateMobileVariantStepper();
      }
    };

    if (this.mobileVariantStepperMql?.addEventListener) {
      this.mobileVariantStepperMql.addEventListener(
        'change',
        this.onMobileVariantStepperMediaChangeBound
      );
    } else if (this.mobileVariantStepperMql?.addListener) {
      this.mobileVariantStepperMql.addListener(this.onMobileVariantStepperMediaChangeBound);
    }

    this.onMobileVariantStepperMediaChangeBound(this.mobileVariantStepperMql);
  }

  activateMobileVariantStepper() {
    if (this.mobileVariantStepperActive) return;

    const fieldsets = [
      ...this.querySelectorAll('fieldset.product-options[data-input-wrapper]')
    ];
    this.debugLog('stepper_activate_attempt', {
      fieldsets: fieldsets.length,
      width: window.innerWidth
    });
    if (!fieldsets.length) return;

    this.mobileVariantStepperFieldsets = fieldsets;
    this.mobileVariantStepperIndex = Math.min(
      this.mobileVariantStepperIndex || 0,
      fieldsets.length - 1
    );
    this.mobileVariantStepperActive = true;
    this.classList.add('is-mobile-stepper-active');

    if (!this.mobileVariantStepperProgressEl) {
      this.mobileVariantStepperProgressEl = document.createElement('div');
      this.mobileVariantStepperProgressEl.className = 'product-selector__stepper-progress';
      this.mobileVariantStepperProgressEl.setAttribute('aria-live', 'polite');
    }

    if (!this.mobileVariantStepperNav) {
      const nav = document.createElement('div');
      nav.className = 'product-selector__stepper-actions';
      nav.innerHTML = `
        <button type="button" class="button button--text product-selector__stepper-arrow product-selector__stepper-back" aria-label="Previous option">
          <svg width="66" height="64" viewBox="0 0 66 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M0 32H63.3079" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" data-ignore-fill=""></path>
            <path d="M49.98 46.9938C53.7027 38.8007 56.6521 35.3612 64 32.0224C56.4879 28.3248 53.5721 24.8728 49.98 17.0059" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" data-ignore-fill=""></path>
          </svg>
        </button>
        <button type="button" class="button button--text product-selector__stepper-arrow product-selector__stepper-next" aria-label="Next option">
          <svg width="66" height="64" viewBox="0 0 66 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M0 32H63.3079" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" data-ignore-fill=""></path>
            <path d="M49.98 46.9938C53.7027 38.8007 56.6521 35.3612 64 32.0224C56.4879 28.3248 53.5721 24.8728 49.98 17.0059" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" data-ignore-fill=""></path>
          </svg>
        </button>
      `;
      this.mobileVariantStepperNav = nav;

      this.onMobileVariantStepperPrevBound = () => {
        if (!this.mobileVariantStepperActive) return;
        this.mobileVariantStepperIndex = Math.max(0, this.mobileVariantStepperIndex - 1);
        this.updateMobileVariantStepperUI(false);
      };

      this.onMobileVariantStepperNextBound = () => {
        if (!this.mobileVariantStepperActive) return;

        const currentFieldset = this.mobileVariantStepperFieldsets[this.mobileVariantStepperIndex];
        const hasSelection = this.fieldsetHasSelection(currentFieldset);
        if (!hasSelection) return;

        this.mobileVariantStepperIndex = Math.min(
          this.mobileVariantStepperFieldsets.length - 1,
          this.mobileVariantStepperIndex + 1
        );
        this.updateMobileVariantStepperUI(false);
      };

      nav
        .querySelector('.product-selector__stepper-back')
        ?.addEventListener('click', this.onMobileVariantStepperPrevBound);
      nav
        .querySelector('.product-selector__stepper-next')
        ?.addEventListener('click', this.onMobileVariantStepperNextBound);
    }

    this.updateMobileVariantStepperUI(false);
    this.classList.add('is-mobile-stepper-ready');
  }

  deactivateMobileVariantStepper() {
    if (!this.mobileVariantStepperActive) return;
    this.debugLog('stepper_deactivate', { width: window.innerWidth });

    this.mobileVariantStepperActive = false;
    this.classList.remove('is-mobile-stepper-active');
    this.classList.remove('is-mobile-stepper-ready');
    this.mobileVariantStepperFieldsets.forEach(fieldset =>
      fieldset.classList.remove('is-stepper-hidden')
    );

    this.querySelectorAll('legend.product-selector__label').forEach(legend => {
      const left = legend.querySelector('.product-selector__stepper-left');
      const center = legend.querySelector('.product-selector__stepper-center');
      const right = legend.querySelector('.product-selector__stepper-right');

      const title = center?.querySelector('.product-selector__legend-title');
      if (title) {
        legend.insertBefore(title, legend.firstChild);
      }

      const sizeGuide = right?.querySelector('.product__size-guide');
      if (sizeGuide) {
        legend.appendChild(sizeGuide);
      }

      left?.remove();
      center?.remove();
      right?.remove();
    });

    this.mobileVariantStepperNav?.remove();
    this.mobileVariantStepperProgressEl?.remove();
  }

  updateMobileVariantStepperUI(shouldScroll) {
    if (!this.mobileVariantStepperActive) return;

    const total = this.mobileVariantStepperFieldsets.length;
    if (!total) return;

    const index = Math.min(Math.max(0, this.mobileVariantStepperIndex), total - 1);
    this.mobileVariantStepperIndex = index;

    this.mobileVariantStepperFieldsets.forEach((fieldset, idx) => {
      fieldset.classList.toggle('is-stepper-hidden', idx !== index);
    });

    const nav = this.mobileVariantStepperNav;
    const progress = this.mobileVariantStepperProgressEl;
    if (!nav || !progress) return;

    const currentFieldset = this.mobileVariantStepperFieldsets[index];
    const legend = currentFieldset?.querySelector('legend.product-selector__label');
    if (legend) {
      nav.remove();
      progress.remove();

      let left = legend.querySelector('.product-selector__stepper-left');
      if (!left) {
        left = document.createElement('div');
        left.className = 'product-selector__stepper-left';
      }

      let center = legend.querySelector('.product-selector__stepper-center');
      if (!center) {
        center = document.createElement('div');
        center.className = 'product-selector__stepper-center';
      }

      let right = legend.querySelector('.product-selector__stepper-right');
      if (!right) {
        right = document.createElement('div');
        right.className = 'product-selector__stepper-right';
      }

      if (!legend.contains(left) || !legend.contains(center) || !legend.contains(right)) {
        legend.appendChild(left);
        legend.appendChild(center);
        legend.appendChild(right);
      }

      const title = legend.querySelector('.product-selector__legend-title');
      if (title && !center.contains(title)) {
        center.appendChild(title);
      }

      const sizeGuide = legend.querySelector('.product__size-guide');
      if (sizeGuide && !right.contains(sizeGuide)) {
        right.appendChild(sizeGuide);
      }

      left.appendChild(progress);
      right.appendChild(nav);
    }

    progress.textContent = `${index + 1} / ${total}`;

    const backBtn = nav.querySelector('.product-selector__stepper-back');
    if (backBtn) backBtn.disabled = index === 0;

    const nextBtn = nav.querySelector('.product-selector__stepper-next');
    if (nextBtn) {
      const hasSelection = this.fieldsetHasSelection(currentFieldset);
      nextBtn.disabled = index >= total - 1 || !hasSelection;
    }

    this.syncAddButtonState();
  }

  disconnectedCallback() {
    if (this.onVariantChangeBound) {
      this.removeEventListener('change', this.onVariantChangeBound);
    }
    if (this.onClickFallbackBound) {
      this.removeEventListener('click', this.onClickFallbackBound);
    }
    if (this.onOptionItemClickBound) {
      this.removeEventListener('click', this.onOptionItemClickBound);
    }
    if (this._onAnyMediaInteractBound) {
      document.removeEventListener('click', this._onAnyMediaInteractBound, true);
    }
    if (this._cartUpdatedListener) {
      document.removeEventListener('cart:updated', this._cartUpdatedListener);
    }
    clearTimeout(this.clickFallbackTimeoutId);
    this.clickFallbackTimeoutId = null;

    if (this.renderAbortController) {
      try {
        this.renderAbortController.abort();
      } catch (e) {}
    }
    this.renderAbortController = null;

    this.dropdownWrappers.forEach(wrapper => {
      const listeners = this.dropdownListeners.get(wrapper);
      if (!listeners) return;
      wrapper.removeEventListener('click', listeners.onClick);
      wrapper.removeEventListener('change', listeners.onChange);
    });
    this.dropdownListeners.clear();
    this.dropdownWrappers = [];

    if (this.onWindowClickCloseDropdownBound) {
      window.removeEventListener('click', this.onWindowClickCloseDropdownBound);
      this.onWindowClickCloseDropdownBound = null;
    }

    if (this.mobileVariantStepperNav) {
      this.mobileVariantStepperNav
        .querySelector('.product-selector__stepper-back')
        ?.removeEventListener('click', this.onMobileVariantStepperPrevBound);
      this.mobileVariantStepperNav
        .querySelector('.product-selector__stepper-next')
        ?.removeEventListener('click', this.onMobileVariantStepperNextBound);
    }

    if (this.mobileVariantStepperMql && this.onMobileVariantStepperMediaChangeBound) {
      if (this.mobileVariantStepperMql.removeEventListener) {
        this.mobileVariantStepperMql.removeEventListener(
          'change',
          this.onMobileVariantStepperMediaChangeBound
        );
      } else if (this.mobileVariantStepperMql.removeListener) {
        this.mobileVariantStepperMql.removeListener(this.onMobileVariantStepperMediaChangeBound);
      }
    }
  }

  onClickFallback(event) {
    const target = event.target;
    if (!target || !target.closest) return;
    const input = target.closest('input[type="radio"], input[data-custom-strap-input]');
    if (!input || input.disabled) return;

    this.didChangeSinceClick = false;
    clearTimeout(this.clickFallbackTimeoutId);
    this.clickFallbackTimeoutId = setTimeout(() => {
      if (this.didChangeSinceClick) return;
      this.onVariantChange({ target: input });
    }, 0);
  }

  onOptionItemClick(event) {
    const target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('input, label, a, button')) return;

    const item = target.closest('.product-options__item');
    if (!item || !this.contains(item)) return;

    const input = item.querySelector('input[type="radio"], input[data-custom-strap-input]');
    if (!input || input.disabled) return;

    if (input.type === 'radio' && this.isStrapRequired() && !this.hasStrapSelection()) {
      const message = window.theme?.strings?.selectStrapFirst || 'Please select a strap first';
      alert(message);
      return;
    }

    input.click();
  }

  syncAddButtonState() {
    if (!this.form) return;

    if (!this.variants.some(v => v.available)) {
      this.form.toggleAddButton(true, window.theme?.strings?.soldOut || 'Sold out');
      return;
    }

    if (!this.currentVariant) {
      this.form.toggleAddButton(true, '');
      return;
    }

    if (!this.currentVariant.available) {
      this.form.toggleAddButton(true, window.theme?.strings?.soldOut || '');
      return;
    }

    if (this._cartVariantIds?.has(this.currentVariant.id)) {
      // Real Shopify inventory doesn't get reduced just by something
      // sitting in a cart (confirmed live) -- this is a deliberate,
      // separate business rule: don't let a variant already in the
      // cart be added again until it's removed. Checked against
      // this._cartVariantIds, a cached snapshot of the real /cart.js
      // contents kept fresh by refreshCartVariantIds() (constructor,
      // and every cart:updated), so this also holds correctly across a
      // page refresh -- not just the moment right after clicking Add
      // to Cart.
      this.form.toggleAddButton(true, window.theme?.strings?.soldOut || 'Sold out');
      return;
    }

    if (this.requireExplicitVariantSelection && !this.hasSelectedAllCoreVariantOptions()) {
      this.form.toggleAddButton(true, '');
      return;
    }

    if (this.mobileVariantStepperActive) {
      const fieldsets = this.mobileVariantStepperFieldsets || [];
      const lastCoreIndex = fieldsets.reduce((acc, fieldset, idx) => {
        if (
          fieldset?.hasAttribute?.('data-custom-strap-wrapper') ||
          fieldset?.hasAttribute?.('data-custom-bottoms-wrapper')
        ) {
          return acc;
        }
        return idx;
      }, -1);

      if (lastCoreIndex >= 0 && this.mobileVariantStepperIndex < lastCoreIndex) {
        this.form.toggleAddButton(true, '');
        return;
      }

      if (!this.hasSelectedAllCoreVariantOptions()) {
        this.form.toggleAddButton(true, '');
        return;
      }
    } else if (this.isStrapRequired() && !this.hasStrapSelection()) {
      this.form.toggleAddButton(true, '');
      return;
    }

    this.form.toggleAddButton(false, '');
    this.updateStrapLegendTitle();
  }

  updateVariantStatuses() {
    const inputWrappers = this.getVariantInputWrappers();

    const applyStatuses = () => {
      inputWrappers.forEach((optionWrapper, optionIndex) => {
      const selectedValues = inputWrappers.map((wrapper, idx) => {
        if (idx === optionIndex) return null; // Bu option serbest
        const checked = wrapper.querySelector(':checked');
        return checked ? this.normalizeValue(checked.value) : null;
      });

      const matchingAvailableVariants = this.variants.filter(variant => {
        if (!variant.available) return false;
        return selectedValues.every((val, idx) => {
          if (val === null) return true;
          return this.normalizeValue(variant[`option${idx + 1}`]) === val;
        });
      });

      const availableValuesForSelection = matchingAvailableVariants.map(
        variant => this.normalizeValue(variant[`option${optionIndex + 1}`])
      );

      const optionInputs = [
        ...optionWrapper.querySelectorAll('input[type="radio"], option')
      ];

      optionInputs.forEach(input => {
        const value = this.normalizeValue(input.value || input.getAttribute('value'));
        if (input.getAttribute('data-stock')) {
          return;
        } else {
          // Server-side rendering (main-product__variant-picker.liquid)
          // now computes this same availability up front, so on a
          // normal page load this is almost always already correct.
          // Skip the write when it is: even a same-value assignment
          // still mutates the disabled attribute, which retriggers
          // style recalculation for every `:has(...:disabled)` rule
          // in base.css -- visible as a brief flash/flicker on every
          // load even though nothing actually changed.
          const shouldBeDisabled = !availableValuesForSelection.includes(value);
          if (input.disabled !== shouldBeDisabled) {
            input.disabled = shouldBeDisabled;
          }
        }
        input.removeAttribute('data-unavailable');
        input.removeAttribute('data-soldout');
        if (input.tagName.toLowerCase() === 'option') {
          const escapedValue = input.value.replace(/"/g, '\"');
          const selector = `button[data-value="${escapedValue}"]`;
          const button = input.closest('dropdown-input').querySelector(selector);
          if (button) {
            button.disabled = !availableValuesForSelection.includes(value);
            button.removeAttribute('data-unavailable');
            button.removeAttribute('data-soldout');
          }
        }
        if (!this.productBar) return;
        const inputId = input.dataset.id || input.id;
        const productBarInput = this.productBar.querySelector(
          `option[data-id="${inputId}"]`
        );
        if (!productBarInput) return;
        productBarInput.disabled = input.disabled;
      });

      if (!availableValuesForSelection.length) {
        if (this.requireExplicitVariantSelection && !this.hasAnyVariantOptionSelected()) {
          return;
        }
        if (this.requireExplicitVariantSelection && !this.hasSelectedAllVariantOptions()) {
          return;
        }
        const firstAvailable = this.variants.find(v => v.available);
        if (firstAvailable) {
          this.isAdjusting = true;
          this.applyVariantSelection(firstAvailable);
          this.isAdjusting = false;
          this.updateVariant();
        }
      }
      });
    };

    applyStatuses();
    if (this.ensureValidSelections()) {
      applyStatuses();
    }
  }

  ensureValidSelections() {
    if (this.requireExplicitVariantSelection && !this.hasAnyVariantOptionSelected()) {
      return false;
    }
    const wrappers = this.getVariantInputWrappers();
    let changed = false;

    wrappers.forEach(wrapper => {
      const radios = [...wrapper.querySelectorAll('input[type="radio"]')];
      if (!radios.length) return;

      const checked = radios.find(r => r.checked);
      if (checked && !checked.disabled) return;
      if (!checked && this.requireExplicitVariantSelection) return;

      const firstEnabled = radios.find(r => !r.disabled);
      if (!firstEnabled) return;

      if (checked !== firstEnabled) {
        firstEnabled.checked = true;
        changed = true;
      }
    });

    if (changed) {
      this.isAdjusting = true;
      this.updateOptions();
      this.updateVariant();
      this.isAdjusting = false;
    }

    return changed;
  }

  getSelectedValuesByIndex() {
    const wrappers = this.getVariantInputWrappers();
    return wrappers.map(wrapper => {
      const radio = wrapper.querySelector(
        'input[name^="options["][type="radio"]:checked'
      );
      if (radio) {
        const value = this.normalizeValue(radio.value);
        return value || null;
      }
      const select = wrapper.querySelector('select[name^="options["]');
      if (select) {
        const value = this.normalizeValue(select.value);
        return value || null;
      }
      return null;
    });
  }

  isStyleChangeTarget(target) {
    const wrapper = target?.closest?.(this.inputWrapper);
    if (!wrapper) return false;
    if (this.variantTabsStyleFieldset && wrapper === this.variantTabsStyleFieldset) return true;
    const legendText = (
      wrapper.querySelector('.product-selector__legend-title')?.textContent || ''
    )
      .toString()
      .trim()
      .toLowerCase();
    return legendText.includes('style');
  }

  isColorChangeTarget(target) {
    const wrapper = target?.closest?.(this.inputWrapper);
    if (!wrapper) return false;
    const legendText = (
      wrapper.querySelector('.product-selector__legend-title')?.textContent || ''
    )
      .toString()
      .trim()
      .toLowerCase();
    return legendText.includes('color') || legendText.includes('colour');
  }

  autoSelectFirstColorForStyle(styleValueRaw, { commit = true } = {}) {
    if (!this.requireExplicitVariantSelection) return false;
    const styleIndex = this.getStyleOptionIndex?.() ?? -1;
    const colorIndex = this.getColorOptionIndex?.() ?? -1;
    if (styleIndex < 0 || colorIndex < 0) return false;

    const styleValue = this.normalizeValue(styleValueRaw).toLowerCase();
    if (!styleValue) return false;

    const selectedValues = this.getSelectedValuesByIndex();
    const candidates = this.variants
      .filter(variant => variant.available)
      .filter(variant => {
        if (
          this.normalizeValue(variant[`option${styleIndex + 1}`]).toLowerCase() !==
          styleValue
        ) {
          return false;
        }
        for (let i = 0; i < selectedValues.length; i++) {
          if (i === colorIndex) continue;
          const v = selectedValues[i];
          if (!v) continue;
          if (
            this.normalizeValue(variant[`option${i + 1}`]).toLowerCase() !==
            this.normalizeValue(v).toLowerCase()
          ) {
            return false;
          }
        }
        return true;
      });

    const chosen = candidates[0];
    if (!chosen) return false;
    const desiredColor = this.normalizeValue(
      chosen[`option${colorIndex + 1}`]
    ).toLowerCase();
    if (!desiredColor) return false;

    const prevPreview = this._stylePreviewColor;
    const nextPreview = { style: styleValue, color: desiredColor };
    const previewChanged =
      !prevPreview ||
      prevPreview.style !== nextPreview.style ||
      prevPreview.color !== nextPreview.color;
    this._stylePreviewColor = nextPreview;

    if (!commit) {
      return previewChanged;
    }

    const wrappers = this.getVariantInputWrappers();
    const colorWrapper = wrappers[colorIndex];
    if (!colorWrapper) return false;

    const radios = [...colorWrapper.querySelectorAll('input[type="radio"]')];
    const targetRadio = radios.find(
      input =>
        !input.disabled &&
        this.normalizeValue(input.value).toLowerCase() === desiredColor
    );
    if (!targetRadio) return false;

    const didChange = !targetRadio.checked;
    if (!didChange) return previewChanged;

    this.isAdjusting = true;
    radios.forEach(r => {
      r.checked = r === targetRadio;
      r.removeAttribute('checked');
    });
    const select = colorWrapper.querySelector('select[name^="options["]');
    if (select) {
      select.value = targetRadio.value;
    }
    this.isAdjusting = false;
    return true;
  }

  findClosestVariantFromChangeTarget(target) {
    if (!target) return null;
    const wrappers = this.getVariantInputWrappers();
    const wrapper = target.closest(this.inputWrapper);
    if (!wrapper) return null;
    const changedIndex = wrappers.indexOf(wrapper);
    if (changedIndex < 0) return null;
    const desiredValue = target.value;
    if (!desiredValue) return null;

    const desired = this.normalizeValue(desiredValue);
    const selectedValues = this.getSelectedValuesByIndex();
    const candidates = this.variants.filter(
      variant =>
        variant.available &&
        this.normalizeValue(variant[`option${changedIndex + 1}`]) === desired
    );

    if (!candidates.length) {
      return null;
    }

    const score = variant => {
      let s = 0;
      for (let i = 0; i < selectedValues.length; i++) {
        if (i === changedIndex) continue;
        if (!selectedValues[i]) continue;
        if (this.normalizeValue(variant[`option${i + 1}`]) === selectedValues[i]) s += 2;
      }
      return s;
    };

    const bestVariant = candidates
      .slice()
      .sort((a, b) => score(b) - score(a))[0];

    return bestVariant;
  }

  applyVariantSelection(variant) {
    const wrappers = this.getVariantInputWrappers();
    wrappers.forEach((wrapper, i) => {
      const desiredValue = variant[`option${i + 1}`];
      if (!desiredValue) return;
      const desired = this.normalizeValue(desiredValue);

      const radio = [...wrapper.querySelectorAll('input[type="radio"]')].find(
        input => this.normalizeValue(input.value) === desired
      );
      if (radio) {
        radio.checked = true;
        return;
      }

      const select = wrapper.querySelector('select');
      if (select) {
        const option = [...select.options].find(
          opt => this.normalizeValue(opt.value) === desired
        );
        if (option) {
          select.value = option.value;
        }
      }
    });

    this.updateOptions();
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;
    this.didChangeSinceClick = true;
    if (this.isAdjusting) return;
    const isCustomStrapChange = Boolean(
      event.target?.matches?.('input[data-custom-strap-input]')
    );
    const isCustomBottomsChange = Boolean(
      event.target?.matches?.('input[data-custom-bottoms-input]')
    );
    // #region debug-point A:variant-change-start
    window.__traeDbgPdpBlipClicks?.('A', 'onVariantChange:start', {
      isCustomProduct: this.isCustomProduct,
      requireExplicitVariantSelection: this.requireExplicitVariantSelection,
      target: {
        tag: event.target?.tagName,
        type: event.target?.type,
        name: event.target?.name,
        value: event.target?.value,
        checked: event.target?.checked,
        dataset: {
          optionValue: event.target?.dataset?.optionValue,
          customStrapProductId: event.target?.dataset?.customStrapProductId,
          customBottomsProductId: event.target?.dataset?.customBottomsProductId
        }
      },
      currentVariantId: this.currentVariant?.id || null
    });
    // #endregion
    if (isCustomBottomsChange) {
      try {
        event.stopImmediatePropagation?.();
        event.stopPropagation?.();
      } catch (e) {}
      this.syncAddButtonState();
      this.updateMobileVariantStepperUI(false);
      this.updateVariantTabsInteractivity();
      // #region debug-point A:bottoms-change-end
      window.__traeDbgPdpBlipClicks?.('A', 'onVariantChange:bottoms:end', {
        selectedBottomsProductIds: this.getSelectedCustomBottomsProductIds?.()
          ? this.getSelectedCustomBottomsProductIds()
          : []
      });
      // #endregion
      return;
    }
    const prevVariantId = this.currentVariant?.id || null;
    const prevOptionsKey = JSON.stringify(this.options || []);
    const prevSelectedColor = this.getSelectedColorValue();
    const prevAvailability = typeof this.currentVariant?.available === 'boolean' ? this.currentVariant.available : null;
    const prevSelectedStyle = this.getSelectedStyleValue();
    this.updateOptions();
    const nextSelectedStyle = this.getSelectedStyleValue();
    const nextSelectedColorAfterOptions = this.getSelectedColorValue();
    if (this.isColorChangeTarget(event.target) && nextSelectedColorAfterOptions) {
      this._stylePreviewColor = null;
    }
    if (this.requireExplicitVariantSelection && nextSelectedStyle) {
      const styleChanged = nextSelectedStyle !== prevSelectedStyle;
      if (styleChanged) {
        const colorIndex = this.getColorOptionIndex?.() ?? -1;
        const sizeIndex = this.getSizeOptionIndex?.() ?? -1;
        if (colorIndex >= 0) {
          this.clearOptionSelectionByIndex(colorIndex);
        }
        if (sizeIndex >= 0) {
          this.clearOptionSelectionByIndex(sizeIndex);
        }
        this.updateOptions();
        this.updateMobileVariantStepperUI(false);
        this.updateVariantTabsInteractivity();
      }

      if (!this.getSelectedColorValue()) {
        this.autoSelectFirstColorForStyle(nextSelectedStyle, { commit: false });
      }
    }
    const nextOptionsKey = JSON.stringify(this.options || []);
    if (isCustomStrapChange) {
      const selectedStraps = this.getSelectedCustomStrapInputs();
      if (event.target.checked && selectedStraps.length > 3) {
        event.target.checked = false;
        const item = event.target.closest('.product-options__item');
        if (item) item.classList.remove('is-active');
        this.updateOptions();
        alert(window.theme?.strings?.max_straps || 'You can only select up to 3 straps.');
        return;
      }
      if (event.target.checked) {
        const mediaId = Number(event.target.dataset?.customStrapMediaId || '');
        if (Number.isFinite(mediaId)) {
          this.lastSelectedCustomStrapMediaId = mediaId;
        }
      } else if (
        Number(this.lastSelectedCustomStrapMediaId) ===
        Number(event.target.dataset?.customStrapMediaId || '')
      ) {
        this.lastSelectedCustomStrapMediaId = this.getSelectedCustomStrapMediaId();
      }
      this.syncAddButtonState();
      try {
        this.updateMedia();
      } catch (error) {
        this.debugLog('updateMedia_error', {
          message: error?.message,
          stack: error?.stack
        });
      }
      this.updateMobileVariantStepperUI(false);
      this.scrollToSelectedCustomStrapMedia();
      this.updateStrapLegendTitle();
      this.updateVariantTabsInteractivity();
      // #region debug-point A:strap-change-end
      window.__traeDbgPdpBlipClicks?.('A', 'onVariantChange:strap:end', {
        selectedStrapProductIds: this.getSelectedCustomStrapProductIds(),
        lastSelectedCustomStrapMediaId: this.lastSelectedCustomStrapMediaId || null
      });
      // #endregion
      return;
    }
    const nextSelectedColor = this.getSelectedColorValue();
    let nextVariant = this.getVariantData();

    if (!nextVariant) {
      const adjusted = this.findClosestVariantFromChangeTarget(event.target);
      if (adjusted) {
        this.isAdjusting = true;
        this.applyVariantSelection(adjusted);
        this.isAdjusting = false;
        nextVariant = this.getVariantData() || adjusted;
      }
    }

    const nextVariantId = nextVariant?.id || null;
    this.debugLog('change', {
      target: {
        tag: event.target?.tagName,
        type: event.target?.type,
        name: event.target?.name,
        value: event.target?.value,
        optionValue: event.target?.dataset?.optionValue
      },
      prevVariantId,
      nextVariantId,
      options: this.options
    });
    if (
      prevVariantId === nextVariantId &&
      prevSelectedColor === nextSelectedColor &&
      prevOptionsKey === nextOptionsKey
    ) {
      this.updateVariantTabsInteractivity();
      this.syncAddButtonState();
      // #region debug-point A:variant-change-noop
      window.__traeDbgPdpBlipClicks?.('A', 'onVariantChange:noop', {
        prevVariantId,
        nextVariantId,
        prevSelectedColor,
        nextSelectedColor,
        prevOptionsKey,
        nextOptionsKey
      });
      // #endregion
      return;
    }

    this.updateVariant();
    const nextAvailability = typeof this.currentVariant?.available === 'boolean' ? this.currentVariant.available : null;
    this.debugLog('availability', {
      prevVariantId,
      nextVariantId: this.currentVariant?.id || null,
      prevAvailability,
      nextAvailability
    });
    this.updateVariantStatuses();
    this.productBarUpdateOptions();
    this.updatePickupAvailability();
    if (this.form) {
      // this.form.removeAttribute('data-has-selling-plan'); // removing this is causing issue for selling plans
      this.form.handleErrorMessage();
    }

    if (!this.currentVariant) {
      this.setUnavailable();
      this.syncAddButtonState();
      this.updateVariantTabsInteractivity();

      return;
    }

    this.syncAddButtonState();

    const isSizeChange = (event.target?.name || '')
      .toString()
      .toLowerCase()
      .includes('size');
    if (this.isCustomProduct && isSizeChange) {
      this._strapScrollRequestId += 1;
      this._strapScrollPending = false;
      try {
        const sectionElement =
          this.closest(`[id^="MainProduct-"]`) ||
          this.closest('.js-product') ||
          document;
        const productMedia = sectionElement.querySelector('product-media');
        const activeSlide =
          productMedia?.querySelector('.swiper-slide-active') ||
          productMedia?.querySelector('.swiper-slide');
        const activeMediaId = Number(activeSlide?.getAttribute('data-media-id'));
        if (Number.isFinite(activeMediaId) && activeMediaId) {
          this._preserveActiveMediaOnNextUpdate = true;
          this._preserveActiveMediaId = activeMediaId;
          // #region debug-point C:preserve-media
          window.__traeDbgPdpBlipClicks?.('C', 'media:preserve-on-size', {
            activeMediaId
          });
          // #endregion
        }
      } catch (e) {}
    } else {
      this._preserveActiveMediaOnNextUpdate = false;
      this._preserveActiveMediaId = null;
    }

    const skipUpdateMediaBecausePreview =
      this.requireExplicitVariantSelection &&
      Number.isFinite(this._previewMediaAt) &&
      this._previewMediaVariantId === (this.currentVariant?.id || null) &&
      Date.now() - this._previewMediaAt < 5000;

    if (!skipUpdateMediaBecausePreview) {
      try {
        this.updateMedia();
      } catch (error) {
        this.debugLog('updateMedia_error', {
          message: error?.message,
          stack: error?.stack
        });
      }
    } else {
      this._lastMediaUpdateAt = this._previewMediaAt;
      this._lastMediaUpdateVariantId = this.currentVariant?.id || null;
    }
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
    this.updateMobileVariantStepperUI(false);
    this.updateVariantTabsInteractivity();
  }

  updateOptions() {
    this.querySelectorAll('input[type="radio"], input[data-custom-strap-input]').forEach(input => {
      input.closest('.product-options__item')?.classList.toggle('is-active', input.checked);
    });
    const next = [];
    this.querySelectorAll('input[name^="options["]:checked').forEach(el => {
      next.push({ name: el.dataset.name || el.name, value: el.value });
    });
    this.querySelectorAll('select[name^="options["]').forEach(el => {
      const value = (el.value || '').trim();
      if (this.requireExplicitVariantSelection && !value) return;
      next.push({ name: el.dataset.name || el.name, value: el.value });
    });
    this.options = next;

    this.updateSelectedValueLabels();
  }

  getSelectedColorValue() {
    const colorOption = this.options?.find(
      opt =>
        opt?.name?.toLowerCase().includes('color') ||
        opt?.name?.toLowerCase().includes('colour')
    );
    return this.normalizeValue(colorOption?.value || '').toLowerCase();
  }

  getSelectedStyleValue() {
    const fieldset = this.variantTabsStyleFieldset;
    if (!fieldset) return '';
    const radio = fieldset.querySelector(
      'input[name^="options["][type="radio"]:checked:not(:disabled)'
    );
    if (radio) return this.normalizeValue(radio.value).toLowerCase();
    const select = fieldset.querySelector('select[name^="options["]');
    if (select) return this.normalizeValue(select.value).toLowerCase();
    return '';
  }

  updateSelectedValueLabels() {
    const labels = this.querySelectorAll('[data-selected-value-for]');
    if (!labels.length) return;

    labels.forEach(labelEl => {
      const optionName = labelEl.getAttribute('data-selected-value-for');
      if (!optionName) return;

      const lookupName = `options[${optionName}]`;
      const selectedOption = this.options?.find(
        opt => opt.name === lookupName || opt.name === optionName
      );
      const value = selectedOption?.value;
      if (!value) {
        labelEl.textContent = '';
        return;
      }

      labelEl.textContent = `Selected: ${value}`;
    });
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        selector => {
          selector.value = this.options.find(
            option => option.name === selector.name
          ).value;
        }
      );
      this.productBar.querySelectorAll('dropdown-input').forEach(
        dropdown => {
          dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');

    if (source && destination) destination.innerHTML = source.innerHTML;

    // Pass the inventory quantity from source to the quantity input
    const inventoryQuantityMatch = source?.textContent.match(/\d+/);
    const inventoryQuantity = inventoryQuantityMatch ? parseInt(inventoryQuantityMatch[0]) : 0;
    this.updateQuantityInput(inventoryQuantity);
  }

  updateQuantityInput(inventoryQuantity) {
    const selectedVariantData = this.getVariantData();
    const quantityInput = document.querySelector('.product-selector__quantity quantity-input input');

    let productUsesInventory = true;

    // check inventory is active
    if (selectedVariantData && selectedVariantData.inventory_management === null) {
      productUsesInventory = false;
    }

    // check sell on sold out is active
    if (selectedVariantData && selectedVariantData.inventory_quantity !== null && inventoryQuantity === 0 && selectedVariantData.available === true) {
      productUsesInventory = false;
    }

    if (quantityInput) {
      if (productUsesInventory) {
        quantityInput.setAttribute('max', inventoryQuantity);
        // set value accordingly to inventory
        if (quantityInput.value > inventoryQuantity) {
          quantityInput.value = inventoryQuantity;
        }
      } else {
        quantityInput.setAttribute('max', '');
      }
    }
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector(
      'pickup-availability'
    );

    if (!pickUpAvailabilityNode || !this.currentVariant) return;

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(
        pickUpAvailabilityNode.dataset.variantId
      );

      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
    // Remember the last genuinely user-picked variant (only while a real
    // selection exists) so that after product-form.js's post-add-to-cart
    // reset clears every option, a later `cart:updated` from removing that
    // same item in the drawer can restore it -- see
    // refreshAvailabilityFromServer() -- instead of leaving the shopper
    // staring at blank swatches they have to re-pick before they can add
    // it back.
    if (this.currentVariant && this.hasAnyVariantOptionSelected()) {
      this._lastKnownVariantId = this.currentVariant.id;
    }
  }

  getVariantData() {
    const selectedValues = this.getSelectedValuesByIndex();
    if (selectedValues.every(v => v === null)) {
      return this.requireExplicitVariantSelection ? null : this.variants[0];
    }

    return this.variants.find(variant => {
      return selectedValues.every((val, idx) => {
        if (!val) return true;
        return this.normalizeValue(variant[`option${idx + 1}`]) === val;
      });
    });
  }

  initUpdateMedia() {

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = parseInt(urlParams.get('variant'));
    this.reportInitialCarouselDebug('initUpdateMedia:start', {
      requireExplicitVariantSelection: this.requireExplicitVariantSelection,
      hasAnyVariantOptionSelected: this.hasAnyVariantOptionSelected(),
      currentVariantId: this.currentVariant?.id || null,
      variantIdFromUrl: Number.isFinite(variantIdFromUrl) ? variantIdFromUrl : null
    });

    if (this.requireExplicitVariantSelection && !this.hasAnyVariantOptionSelected()) {
      this.currentVariant = null;
      // #region debug-point C:initUpdateMedia-no-selection
      try {
        this._dbgSend('H4', 'initUpdateMedia:no_selection', {
          hasAnySelected: false,
          variantIdFromUrl: Number.isFinite(variantIdFromUrl) ? variantIdFromUrl : null
        });
      } catch (e) {}
      // #endregion
      if (urlParams.get('bb_no_media_reset') === '1') {
        // #region debug-point D:bb-no-media-reset
        try {
          this._dbgSend('H4', 'initUpdateMedia:bb_no_media_reset', {});
        } catch (e) {}
        // #endregion
        return;
      }
      setTimeout(() => {
        this.prefilterMediaToFirstStyleAndColor();
      }, 0);
      return;
    }

    if (variantIdFromUrl) {
      const fromUrl = this.variants.find(variant => variant.id === variantIdFromUrl);
      if (fromUrl && fromUrl.available) {
        this.currentVariant = fromUrl;
      } else if (fromUrl && !fromUrl.available) {
        const firstAvailable = this.variants.find(v => v.available);
        if (firstAvailable) {
          this.isAdjusting = true;
          this.applyVariantSelection(firstAvailable);
          this.isAdjusting = false;
          this.updateVariant();
          this.updateURL();
          this.updateVariantInput();
        }
      }
    }
    
    if (this.currentVariant) {
      this.reportInitialCarouselDebug('initUpdateMedia:currentVariant', {
        currentVariantId: this.currentVariant?.id || null,
        requireExplicitVariantSelection: this.requireExplicitVariantSelection,
        hasAnyVariantOptionSelected: this.hasAnyVariantOptionSelected()
      });
      setTimeout(() => {
        this.updateMedia();
      }, 0);
    } else if (this.requireExplicitVariantSelection) {
      this.reportInitialCarouselDebug('initUpdateMedia:resetMediaToAll', {
        currentVariantId: this.currentVariant?.id || null,
        requireExplicitVariantSelection: this.requireExplicitVariantSelection,
        hasAnyVariantOptionSelected: this.hasAnyVariantOptionSelected()
      });
      setTimeout(() => {
        this.resetMediaToAll();
      }, 0);
    }
  }

  getVariantOptionIndexByLegendIncludes(keyword) {
    const needle = (keyword || '').toString().trim().toLowerCase();
    if (!needle) return -1;

    const wrappers = this.getVariantInputWrappers();
    return wrappers.findIndex(wrapper => {
      if (
        wrapper.hasAttribute('data-custom-strap-wrapper') ||
        wrapper.hasAttribute('data-custom-bottoms-wrapper')
      ) {
        return false;
      }
      const legendText = (
        wrapper.querySelector('.product-selector__legend-title')?.textContent || ''
      )
        .toString()
        .trim()
        .toLowerCase();
      return legendText.includes(needle);
    });
  }

  getStyleOptionIndex() {
    const wrappers = this.getVariantInputWrappers();
    if (this.variantTabsStyleFieldset) {
      const idx = wrappers.indexOf(this.variantTabsStyleFieldset);
      if (idx >= 0) return idx;
    }
    return this.getVariantOptionIndexByLegendIncludes('style');
  }

  getColorOptionIndex() {
    const idx = this.getVariantOptionIndexByLegendIncludes('color');
    if (idx >= 0) return idx;
    return this.getVariantOptionIndexByLegendIncludes('colour');
  }

  getSizeOptionIndex() {
    return this.getVariantOptionIndexByLegendIncludes('size');
  }

  clearOptionSelectionByIndex(index) {
    const wrappers = this.getVariantInputWrappers();
    const wrapper = wrappers[index];
    if (!wrapper) return false;

    const radios = [...wrapper.querySelectorAll('input[type="radio"]')];
    const select = wrapper.querySelector('select[name^="options["]');

    const hadRadioChecked = radios.some(r => r.checked);
    const hadSelectValue = Boolean((select?.value || '').trim());
    const hadAny = hadRadioChecked || hadSelectValue;
    if (!hadAny) return false;

    this.isAdjusting = true;
    radios.forEach(r => {
      r.checked = false;
      r.removeAttribute('checked');
    });
    if (select) {
      Array.from(select.options).forEach(opt => opt.removeAttribute('selected'));
      select.value = '';
    }
    wrapper.querySelectorAll('.product-options__item.is-active').forEach(el =>
      el.classList.remove('is-active')
    );
    this.isAdjusting = false;

    return true;
  }

  prefilterMediaToFirstStyleAndColor() {
    const previewVariant = this.variants.find(v => v.available) || this.variants[0];
    if (!previewVariant) {
      this.resetMediaToAll();
      return;
    }

    const styleIndex = this.getStyleOptionIndex();
    const colorIndex = this.getColorOptionIndex();

    const styleValue = styleIndex >= 0
      ? this.normalizeValue(previewVariant[`option${styleIndex + 1}`]).toLowerCase()
      : '';
    const colorValue = colorIndex >= 0
      ? this.normalizeValue(previewVariant[`option${colorIndex + 1}`]).toLowerCase()
      : '';

    const sectionElement =
      this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
    const productMediaRoot = sectionElement.querySelector('product-media');
    const currentSlides = productMediaRoot?.originalSlides?.length
      ? productMediaRoot.originalSlides
      : [...(productMediaRoot?.querySelectorAll('.swiper-wrapper .swiper-slide[data-media-id]') || [])];
    const matchingStyleSlides = currentSlides.filter(slide =>
      this.normalizeValue(slide.getAttribute('data-style')).toLowerCase() === styleValue
    );
    const matchingStyleColorSlides = matchingStyleSlides.filter(slide => {
      const slideColor = this.normalizeValue(slide.getAttribute('data-color')).toLowerCase();
      return !colorValue || slideColor === colorValue;
    });

    this.reportInitialCarouselDebug('prefilterMediaToFirstStyleAndColor', {
      previewVariantId: previewVariant.id || null,
      styleIndex,
      styleValue: styleValue || null,
      colorIndex,
      colorValue: colorValue || null,
      matchingStyleMediaIds: matchingStyleSlides
        .map(slide => Number(slide.getAttribute('data-media-id')) || null)
        .filter(id => Number.isFinite(id)),
      matchingStyleColorMediaIds: matchingStyleColorSlides
        .map(slide => Number(slide.getAttribute('data-media-id')) || null)
        .filter(id => Number.isFinite(id))
    });

    if (!styleValue && !colorValue) {
      this.resetMediaToAll();
      return;
    }

    this._previewMediaVariantId = previewVariant.id || null;
    this._previewMediaAt = Date.now();
    this._previewMediaStyle = styleValue || null;
    this._previewMediaColor = colorValue || null;

    try {
      this.updateMedia({
        variant: previewVariant,
        styleOverride: styleValue || null,
        colorOverride: colorValue || null,
        isPreview: true
      });
    } catch (e) {
      this.resetMediaToAll();
    }
  }

  resetMediaToAll() {
    const sectionElement =
      this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
    const productMediaRoot = sectionElement.querySelector('product-media');
    if (!productMediaRoot) return;

    // #region debug-point B:reset-media-order
    try {
      this._dbgSend('H4', 'resetMediaToAll:start', {
        productMedia: {
          className: productMediaRoot.className || null,
          hasSwiper: Boolean(
            productMediaRoot?.settings?.sliderInstance &&
              !productMediaRoot.settings.sliderInstance.destroyed
          )
        }
      });
    } catch (e) {}
    // #endregion

    productMediaRoot.classList.remove('is-media-updating');
    productMediaRoot.classList.add('is-media-ready');

    const sliderEl = productMediaRoot.querySelector('[data-slider]');
    const thumbsEl = productMediaRoot.querySelector('.swiper-thumbs');
    [sliderEl, thumbsEl].forEach(el => {
      if (!el) return;
      el.style.opacity = '';
      el.style.pointerEvents = '';
      delete el.dataset.prevOpacity;
      delete el.dataset.prevPointerEvents;
    });

    const swiperWrapper = productMediaRoot.querySelector('.swiper-wrapper');
    const thumbWrapper = productMediaRoot.querySelector('.swiper-thumbs .swiper-wrapper');

    if (swiperWrapper) {
      if (!productMediaRoot.originalSlides) {
        productMediaRoot.originalSlides = Array.from(swiperWrapper.children);
      }
      productMediaRoot.originalSlides.forEach(slide => {
        slide.style.display = '';
        slide.classList.remove('is-hidden');
      });
      swiperWrapper.innerHTML = '';
      productMediaRoot.originalSlides.forEach(slide => swiperWrapper.appendChild(slide));
    }

    if (thumbWrapper) {
      if (!productMediaRoot.originalThumbs) {
        productMediaRoot.originalThumbs = Array.from(thumbWrapper.children);
      }
      productMediaRoot.originalThumbs.forEach(slide => {
        slide.style.display = '';
        slide.classList.remove('is-hidden');
      });
      thumbWrapper.innerHTML = '';
      productMediaRoot.originalThumbs.forEach(slide => thumbWrapper.appendChild(slide));
    }

    const firstMediaId = Number(
      swiperWrapper?.querySelector('[data-media-id]')?.getAttribute('data-media-id') || ''
    );
    if (Number.isFinite(firstMediaId) && typeof productMediaRoot.setActiveMedia === 'function') {
      productMediaRoot.setActiveMedia(firstMediaId);
    } else if (productMediaRoot.settings?.sliderInstance && !productMediaRoot.settings.sliderInstance.destroyed) {
      try {
        productMediaRoot.settings.sliderInstance.slideTo(0, 0, false);
      } catch (e) {}
    }

    if (typeof productMediaRoot.refreshSwiperLayout === 'function') {
      productMediaRoot.refreshSwiperLayout();
    }
  }

  updateMedia(params = {}) {
    const isPreview = Boolean(params && params.isPreview);
    const variant = params?.variant || this.currentVariant;
    if (!variant) return;

    const styleOverride = params?.styleOverride;
    const colorOverride = params?.colorOverride;

    const mediaUpdateStartedAt = Date.now();
    const mediaUpdateTraceId = `${mediaUpdateStartedAt}-${Math.random()
      .toString(16)
      .slice(2)}`;
    this._mediaUpdateTraceId = mediaUpdateTraceId;
    if (!isPreview) {
      this._lastMediaUpdateAt = mediaUpdateStartedAt;
      this._lastMediaUpdateVariantId = variant?.id || null;
    }

    // Apply color filtering
    const colorOption = this.options?.find(opt => opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('colour'));
    const selectedColorRaw =
      typeof colorOverride === 'string'
        ? colorOverride
        : (colorOption ? colorOption.value : '');
    let selectedColor = this.normalizeValue(selectedColorRaw).toLowerCase().trim() || null;
    const selectedStyleRaw =
      typeof styleOverride === 'string' ? styleOverride : (this.getSelectedStyleValue() || '');
    const selectedStyle = this.normalizeValue(selectedStyleRaw).toLowerCase().trim() || null;

    if (
      !selectedColor &&
      !colorOverride &&
      this.requireExplicitVariantSelection &&
      selectedStyle &&
      this._stylePreviewColor &&
      this._stylePreviewColor.style === selectedStyle &&
      this._stylePreviewColor.color
    ) {
      selectedColor = this._stylePreviewColor.color;
    }
    this.debugMediaSnapshot('updateMedia:start');

    // #region debug-point B:carousel-flash-start
    try {
      this._dbgSend('H1', 'updateMedia:start', {
        traceId: mediaUpdateTraceId,
        selectedStyle,
        selectedColor,
        variantId: variant?.id || null
      });
    } catch (e) {}
    // #endregion

    // #region debug-point D:updateMedia-start
    window.__traeDbgPdpBlipClicks?.('D', 'updateMedia:start', {
      traceId: mediaUpdateTraceId,
      variantId: variant?.id || null,
      featuredMediaId: variant?.featured_media?.id || null,
      selectedColor: selectedColor || null,
      options: (this.options || []).map(o => ({ name: o?.name, value: o?.value }))
    });
    // #endregion
    
    const sectionElement = this.closest(`[id^="MainProduct-"]`) || this.closest('.js-product') || document;
    const productMediaRoot = sectionElement.querySelector('product-media');

    if (productMediaRoot) {
      productMediaRoot.classList.add('is-media-ready');
      productMediaRoot.classList.add('is-media-updating');
      // #region debug-point B:carousel-flash-updating-on
      try {
        this._dbgSend('H2', 'media_updating:on', {
          traceId: mediaUpdateTraceId,
          productMediaClassName: productMediaRoot.className || null
        });
      } catch (e) {}
      // #endregion
      const sliderEl = productMediaRoot.querySelector('[data-slider]');
      const thumbsEl = productMediaRoot.querySelector('.swiper-thumbs');
      [sliderEl, thumbsEl].forEach(el => {
        if (!el) return;
        el.style.opacity = '';
        el.style.pointerEvents = '';
        delete el.dataset.prevOpacity;
        delete el.dataset.prevPointerEvents;
      });
    }

    const hasCustomStrapSlides = Boolean(
      productMediaRoot?.querySelector?.('[data-custom-strap="true"]')
    );
    if (
      selectedStyle ||
      selectedColor ||
      this.querySelector('input[data-custom-strap-input]') ||
      hasCustomStrapSlides
    ) {
      const productMedia = productMediaRoot;
      let wellnessGroupMediaIds = null;
      if (productMedia) {
        const isWellnessTemplate =
          productMedia.getAttribute('data-wellness-template') === 'true';
        const setUpdating = isUpdating => {
          productMedia.classList.toggle('is-media-updating', isUpdating);
            // #region debug-point B:carousel-flash-updating-toggle
            try {
              this._dbgSend('H2', 'media_updating:toggle', {
                traceId: mediaUpdateTraceId,
                isUpdating,
                productMediaClassName: productMedia.className || null
              });
            } catch (e) {}
            // #endregion
        };
        const swiperWrapper = productMedia.querySelector('.swiper-wrapper');
        const thumbWrapper = productMedia.querySelector('.swiper-thumbs .swiper-wrapper');
        
        if (swiperWrapper && !productMedia.originalSlides) {
          productMedia.originalSlides = Array.from(swiperWrapper.children);
        }
        if (thumbWrapper && !productMedia.originalThumbs) {
          productMedia.originalThumbs = Array.from(thumbWrapper.children);
        }

        const sameSlideOrder = (a, b) => {
          if (!Array.isArray(a) || !Array.isArray(b)) return false;
          if (a.length !== b.length) return false;
          for (let i = 0; i < a.length; i++) {
            const aId = Number(a[i]?.getAttribute?.('data-media-id'));
            const bId = Number(b[i]?.getAttribute?.('data-media-id'));
            if (aId !== bId) return false;
          }
          return true;
        };

        const buildVariantSlides = (originalArray, isThumbTrack = false) => {
          if (!originalArray || !originalArray.length) return [];
          const normalize = value => (value ?? '').toString().trim().toLowerCase();
          const customStrapSlides = originalArray.filter(
            slide => slide.getAttribute('data-custom-strap') === 'true'
          );
          // Dynamically create strap slides from strap cards if not in DOM
          if (!customStrapSlides.length) {
            const inputs = [...this.querySelectorAll('input[data-custom-strap-input]:checked')];
            inputs.forEach(input => {
              const card = input.closest('.strap-card');
              if (!card) return;
              const img = card.querySelector('img');
              if (!img) return;
              const refSlide = originalArray.find(
                slide =>
                  slide.getAttribute('data-custom-strap') !== 'true' &&
                  slide.getAttribute('data-custom-bottoms') !== 'true'
              );
              const refMedia = refSlide?.querySelector?.('.media') || null;
              const slide = document.createElement('div');
              slide.className = 'swiper-slide';
              slide.dataset.customStrap = 'true';
              slide.dataset.customStrapProductId = input.dataset.customStrapProductId || '';
              slide.dataset.mediaId = input.dataset.customStrapMediaId || '';
              slide.dataset.mediaType = 'image';
              const mediaDiv = document.createElement('div');
              if (isThumbTrack) {
                mediaDiv.className = 'media media--thumb';
              } else {
                mediaDiv.className = refMedia?.className || 'media';
                const refStyle = refMedia?.getAttribute?.('style');
                if (refStyle) mediaDiv.setAttribute('style', refStyle);
              }
              const clone = img.cloneNode(true);
              clone.removeAttribute('srcset');
              clone.removeAttribute('sizes');
              clone.removeAttribute('width');
              clone.removeAttribute('height');
              clone.removeAttribute('class');
              mediaDiv.appendChild(clone);
              slide.appendChild(mediaDiv);
              customStrapSlides.push(slide);
            });
          }

          const selectedCustomStrapProductIds = this.getSelectedCustomStrapProductIds();
          const selectedCustomStrapProductIdSet = new Set(selectedCustomStrapProductIds);
          const currentVariantId = Number(variant?.id);
          const selectedCustomStrapSlides = customStrapSlides.filter(slide => {
            if (!selectedCustomStrapProductIdSet.size) return false;
            const productId = Number(slide.getAttribute('data-custom-strap-product-id'));
            if (
              Number.isFinite(productId) &&
              !selectedCustomStrapProductIdSet.has(productId)
            ) {
              return false;
            }
            return true;
          });
          this.debugLog('media_strap_selection', {
            isThumbTrack,
            selectedCustomStrapProductIds,
            currentVariantId,
            customStrapSlides: customStrapSlides.length,
            selectedCustomStrapSlides: selectedCustomStrapSlides.length,
            selectedCustomStrapMediaIds: selectedCustomStrapSlides.map(slide =>
              Number(slide.getAttribute('data-media-id')) || null
            )
          });
          originalArray = originalArray.filter(
            slide => slide.getAttribute('data-custom-strap') !== 'true' && slide.getAttribute('data-custom-bottoms') !== 'true'
          );
          if (!originalArray.length) {
            return selectedCustomStrapSlides;
          }
          const variantOptionCount = [
            variant?.option1,
            variant?.option2,
            variant?.option3
          ].filter(value => normalize(value)).length;

          const targetStyle = normalize(selectedStyle);
          if (targetStyle) {
            const styleFiltered = originalArray.filter(
              slide => normalize(slide.getAttribute('data-style')) === targetStyle
            );
            if (styleFiltered.length) {
              originalArray = styleFiltered;
            }
          }
          const ensureDefaultMinSlides = (slides, targetColorForSlides) => {
            if (!Array.isArray(slides)) return [];
            if (variantOptionCount > 1) {
              return slides;
            }
            if (originalArray.length > 1 && slides.length <= 1) {
              return originalArray.slice();
            }
            return slides;
          };
          const colorOptionIndex = this.options.findIndex(
            opt =>
              opt?.name?.toLowerCase().includes('color') ||
              opt?.name?.toLowerCase().includes('colour')
          );
          const targetColor =
            normalize(selectedColor) ||
            (colorOptionIndex >= 0
              ? normalize(variant?.[`option${colorOptionIndex + 1}`])
              : '');
          const finalizeSlides = slides => {
            const base = Array.isArray(slides) ? slides : [];
            let merged = [...base];
            if (!isThumbTrack) {
              const featuredMediaId = Number(variant?.featured_media?.id);
              if (Number.isFinite(featuredMediaId) && featuredMediaId) {
                const featuredIndex = merged.findIndex(slide => {
                  const id = Number(slide?.getAttribute?.('data-media-id'));
                  return id === featuredMediaId;
                });
                if (featuredIndex > 0) {
                  const featured = merged[featuredIndex];
                  merged = [
                    featured,
                    ...merged.slice(0, featuredIndex),
                    ...merged.slice(featuredIndex + 1)
                  ];
                }
              }
            }
            if (selectedCustomStrapSlides.length) {
              merged = [...merged, ...selectedCustomStrapSlides];
            }
            this.debugLog('media_strap_merged', {
              isThumbTrack,
              base: base.length,
              merged: merged.length,
              mergedMediaIds: merged.map(slide =>
                Number(slide.getAttribute('data-media-id')) || null
              )
            });
            if (!isThumbTrack) {
              this.reportInitialCarouselDebug('buildVariantSlides:finalize', {
                previewVariantId: variant?.id || null,
                styleValue: targetStyle || null,
                colorValue: targetColor || null,
                filteredResultMediaIds: merged
                  .map(slide => Number(slide.getAttribute('data-media-id')) || null)
                  .filter(id => Number.isFinite(id))
              });
            }
            return merged;
          };
          if (!targetColor) return finalizeSlides(originalArray.slice());

          const debugMediaMap = originalArray.map((slide, index) => ({
            index,
            mediaId: Number(slide.getAttribute('data-media-id')) || null,
            color: normalize(slide.getAttribute('data-color')),
            variantGroup: normalize(slide.getAttribute('data-variant-group'))
          }));
          const logGrouping = (mode, slides, extra = {}) => {
            this.debugLog('media_grouping', {
              mode,
              selectedColor: normalize(selectedColor),
              targetColor,
              featuredMediaId: variant?.featured_media?.id || null,
              resultMediaIds: (slides || []).map(slide =>
                Number(slide.getAttribute('data-media-id')) || null
              ),
              mediaMap: debugMediaMap,
              ...extra
            });
          };

          const mediaIndexById = new Map();
          originalArray.forEach((slide, index) => {
            const mediaId = Number(slide.getAttribute('data-media-id'));
            if (!Number.isNaN(mediaId)) mediaIndexById.set(mediaId, index);
          });
          const featuredMediaIdCurrent = Number(variant?.featured_media?.id);
          const featuredIndexCurrent = mediaIndexById.has(featuredMediaIdCurrent)
            ? mediaIndexById.get(featuredMediaIdCurrent)
            : -1;

          const hasColorOpt = this.options?.some(opt => normalize(opt.name) === 'color' || normalize(opt.name) === 'colour');
          const hasStrapOpt = this.options?.some(opt => normalize(opt.name) === 'strap');

          if (hasColorOpt && hasStrapOpt) {
            // Find the current variant group name (e.g. "green polkadot-emerald gold")
            const colorOptVal = this.options.find(opt => normalize(opt.name) === 'color' || normalize(opt.name) === 'colour')?.value;
            const strapOptVal = this.options.find(opt => normalize(opt.name) === 'strap')?.value;
            
            if (colorOptVal && strapOptVal) {
              const targetVariantGroup = normalize(colorOptVal) + '-' + normalize(strapOptVal);
              
              // Filter slides that exactly match this group
              const exactGroupSlides = originalArray.filter(slide => {
                return normalize(slide.getAttribute('data-variant-group')) === targetVariantGroup;
              });

              if (exactGroupSlides.length > 0) {
                return finalizeSlides(exactGroupSlides);
              }
            }
          }

          let startIndex = featuredIndexCurrent;
          if (startIndex < 0 && targetColor) {
            const firstColorSlideIndex = originalArray.findIndex(slide => normalize(slide.getAttribute('data-color')) === targetColor);
            if (firstColorSlideIndex >= 0) {
              startIndex = firstColorSlideIndex;
            }
          }

          if (startIndex >= 0 && (!hasColorOpt || !hasStrapOpt)) {
            const allFeaturedIndices = Array.from(mediaIndexById.entries())
              .filter(([id, index]) => this.variants.some(v => Number(v.featured_media?.id) === id))
              .map(([id, index]) => index)
              .sort((a, b) => a - b);
            const nextFeaturedIndex = allFeaturedIndices.find(index => index > startIndex);
            const end = typeof nextFeaturedIndex === 'number' ? nextFeaturedIndex - 1 : originalArray.length - 1;
            const segmentSlides = [];
            for (let i = startIndex; i <= end; i++) {
              if (originalArray[i]) {
                const c = normalize(originalArray[i].getAttribute('data-color'));
                if (!targetColor || !c || c === targetColor) {
                  segmentSlides.push(originalArray[i]);
                }
              }
            }
            if (segmentSlides.length) {
              return finalizeSlides(segmentSlides);
            }
          }

          const anchorColorsByIndex = new Map();
          this.variants.forEach(variant => {
            const mediaId = Number(variant?.featured_media?.id);
            if (!mediaId || !mediaIndexById.has(mediaId)) return;
            const variantColor = colorOptionIndex >= 0
              ? normalize(variant[`option${colorOptionIndex + 1}`])
              : '';
            if (!variantColor) return;

            const idx = mediaIndexById.get(mediaId);
            const existing = anchorColorsByIndex.get(idx);
            if (!existing) {
              anchorColorsByIndex.set(idx, [variantColor]);
            } else if (!existing.includes(variantColor)) {
              existing.push(variantColor);
            }
          });

          const selectedColorAnchorIds = new Set();
          this.variants.forEach(variant => {
            const variantColor = colorOptionIndex >= 0
              ? normalize(variant[`option${colorOptionIndex + 1}`])
              : '';
            if (!variantColor || variantColor !== targetColor) return;
            const mediaId = Number(variant?.featured_media?.id);
            if (mediaId && mediaIndexById.has(mediaId)) {
              selectedColorAnchorIds.add(mediaId);
            }
          });

          const anchors = Array.from(anchorColorsByIndex.entries())
            .map(([index, colors]) => ({
              index,
              color: colors.includes(targetColor) ? targetColor : colors[0]
            }))
            .sort((a, b) => a.index - b.index);

          const globalAnchorIndices = Array.from(anchorColorsByIndex.keys()).sort((a, b) => a - b);
          const selectedColorAnchorIndices = Array.from(selectedColorAnchorIds)
            .map(mediaId => mediaIndexById.get(mediaId))
            .filter(index => typeof index === 'number')
            .sort((a, b) => a - b);

          if (selectedColorAnchorIndices.length && globalAnchorIndices.length) {
            const strictAnchorSlides = [];
            selectedColorAnchorIndices.forEach(start => {
              const nextGlobal = globalAnchorIndices.find(index => index > start);
              const end = typeof nextGlobal === 'number' ? nextGlobal - 1 : originalArray.length - 1;
              for (let i = start; i <= end; i++) {
                if (originalArray[i]) strictAnchorSlides.push(originalArray[i]);
              }
            });

            const dedupedStrictSlides = Array.from(new Set(strictAnchorSlides));
            if (
              dedupedStrictSlides.length &&
              dedupedStrictSlides.length < originalArray.length
            ) {
              const strictResult = finalizeSlides(dedupedStrictSlides);
              logGrouping('anchors_strict_variant', strictResult, {
                selectedColorAnchorIndices,
                globalAnchorIndices,
                selectedColorAnchorIds: Array.from(selectedColorAnchorIds)
              });
              return strictResult;
            }
          }

          let groupedSlidesByAnchors = [];
          if (anchors.length) {
            for (let i = 0; i < anchors.length; i++) {
              if (anchors[i].color !== targetColor) continue;
              const start = anchors[i].index;
              const end = (i + 1 < anchors.length ? anchors[i + 1].index : originalArray.length) - 1;
              for (let j = start; j <= end; j++) {
                if (originalArray[j]) groupedSlidesByAnchors.push(originalArray[j]);
              }
            }
          }

          const exactColorSlides = originalArray.filter(slide => {
            const c = normalize(slide.getAttribute('data-color'));
            return c === targetColor;
          });
          const exactLooksTooBroad =
            exactColorSlides.length === originalArray.length &&
            groupedSlidesByAnchors.length > 0 &&
            groupedSlidesByAnchors.length < originalArray.length;

          if (exactColorSlides.length && !exactLooksTooBroad) {
            const exactResult = finalizeSlides(exactColorSlides);
            logGrouping('exact', exactResult, { anchors });
            return exactResult;
          }

          if (groupedSlidesByAnchors.length) {
            const groupedResult = finalizeSlides(groupedSlidesByAnchors);
            logGrouping('anchors', groupedResult, { anchors, exactLooksTooBroad });
            return groupedResult;
          }

          if (featuredIndexCurrent >= 0) {
            const fallbackResult = finalizeSlides([originalArray[featuredIndexCurrent]]);
            logGrouping('featured_fallback', fallbackResult, { anchors });
            return fallbackResult;
          }
          const fallbackResult = finalizeSlides([originalArray[0]]);
          logGrouping('first_fallback', fallbackResult, { anchors });
          return fallbackResult;
        };

        if (isWellnessTemplate && productMedia.originalSlides) {
          wellnessGroupMediaIds = new Set(
            buildVariantSlides(productMedia.originalSlides, true)
              .map(slide => Number(slide.getAttribute('data-media-id')))
              .filter(mediaId => Number.isFinite(mediaId))
          );
        }

        const refreshSwiperAfterDomChange = (swiperInstance, isThumbTrack) => {
          if (!swiperInstance || swiperInstance.destroyed) return;
          const n = swiperInstance.slides?.length ?? 0;
          const isWellnessSlider =
            productMedia.getAttribute('data-wellness-slider') === 'true';
          const isWellnessTemplate =
            productMedia.getAttribute('data-wellness-template') === 'true';
          const forceSingleWellnessSlides =
            isWellnessTemplate && !isWellnessSlider;
          const shouldLoopOddMain =
            !isThumbTrack &&
            !forceSingleWellnessSlides &&
            !this.isCustomProduct &&
            n > 2 &&
            n % 2 === 1;

          if (!isThumbTrack && swiperInstance.params) {
            const desktopSlidesPerView = isWellnessTemplate
              ? 1
              : n >= 2
                ? 2
                : 1;
            
            // If there's only 1 slide, center it. Otherwise don't.
            const shouldCenter = n === 1;

            const desktopSlidesPerGroup = 1;
            swiperInstance.params.slidesPerView =
              window.innerWidth >= 750 ? desktopSlidesPerView : 1;
            swiperInstance.params.slidesPerGroup =
              window.innerWidth >= 750 ? desktopSlidesPerGroup : 1;
            swiperInstance.params.spaceBetween = 0;
            swiperInstance.params.centeredSlides = shouldCenter;
            swiperInstance.params.loop = shouldLoopOddMain;
            swiperInstance.params.rewind = !shouldLoopOddMain && n > 1;
            const bps = swiperInstance.params.breakpoints;
            if (bps) {
              Object.keys(bps).forEach(key => {
                const bp = bps[key];
                if (bp) {
                  bp.slidesPerView = desktopSlidesPerView;
                  bp.slidesPerGroup = desktopSlidesPerGroup;
                }
              });
            }
          }

          // Odd-sized variant groups need circular pairing (e.g. 3 -> [1,2],[2,3],[3,1]).
          if (!isThumbTrack) {
            try {
              if (typeof swiperInstance.loopDestroy === 'function') {
                swiperInstance.loopDestroy();
              }
              if (
                shouldLoopOddMain &&
                typeof swiperInstance.loopCreate === 'function'
              ) {
                swiperInstance.loopCreate();
              }
            } catch (e) {
              this.debugLog('loop_toggle_error', { message: e?.message });
            }
          }

          swiperInstance.updateSize();
          swiperInstance.updateSlides();
          swiperInstance.update();

          const singleOrNone = n <= 1;
          swiperInstance.allowTouchMove = shouldLoopOddMain ? n > 1 : !singleOrNone;
          if (swiperInstance.params) {
            swiperInstance.params.watchOverflow = shouldLoopOddMain ? false : !singleOrNone;
          }
          if (!isThumbTrack && swiperInstance.el?.classList) {
            swiperInstance.el.classList.toggle(
              'one-slide',
              singleOrNone
            );
            
            // Force container centering explicitly on variant change when there's 1 slide
            if (singleOrNone && window.innerWidth >= 990) {
              swiperInstance.el.style.marginInlineStart = 'auto';
              swiperInstance.el.style.marginInlineEnd = 'auto';
            } else if (!singleOrNone && window.innerWidth >= 990) {
              swiperInstance.el.style.marginInlineStart = '0';
              swiperInstance.el.style.marginInlineEnd = '';
            }
          }
          swiperInstance.allowSlideNext = !singleOrNone;
          swiperInstance.allowSlidePrev = !singleOrNone;

          if (!isThumbTrack && swiperInstance.navigation) {
            try {
              if (singleOrNone && typeof swiperInstance.navigation.disable === 'function') {
                swiperInstance.navigation.disable();
              } else if (!singleOrNone && typeof swiperInstance.navigation.enable === 'function') {
                swiperInstance.navigation.enable();
              }
              if (typeof swiperInstance.navigation.update === 'function') {
                swiperInstance.navigation.update();
              }
            } catch (e) {
              /* optional */
            }
          }

          try {
            if (
              swiperInstance.pagination &&
              typeof swiperInstance.pagination.render === 'function'
            ) {
              swiperInstance.pagination.render();
              swiperInstance.pagination.update();
            }
          } catch (e) {
            /* progressbar / dynamic slides: optional */
          }
        };

        const filterSlidesSwiper = (originalArray, swiperInstance, isThumbTrack) => {
          if (!originalArray || !swiperInstance || swiperInstance.destroyed) return;
          const variantSlides = buildVariantSlides(originalArray, isThumbTrack);
          swiperInstance.removeAllSlides();
          if (variantSlides.length) {
            swiperInstance.appendSlide(variantSlides);
          }
          refreshSwiperAfterDomChange(swiperInstance, isThumbTrack);
        };

        if (typeof productMedia.cancelQueuedActiveMedia === 'function') {
          productMedia.cancelQueuedActiveMedia();
        }

        if (productMedia.settings && productMedia.settings.sliderInstance) {
          const mainSwiper = productMedia.settings.sliderInstance;
          const thumbsSwiperEl = productMedia.querySelector('.swiper-thumbs');
          const thumbsSwiper = thumbsSwiperEl && thumbsSwiperEl.swiper;

          const nextMainSlides = buildVariantSlides(productMedia.originalSlides, false);
          const nextThumbSlides = productMedia.originalThumbs
            ? buildVariantSlides(productMedia.originalThumbs, true)
            : null;
            
          const isMainDifferent = !sameSlideOrder(nextMainSlides, Array.from(mainSwiper.slides || []));
          const isThumbDifferent = thumbsSwiper && nextThumbSlides && !sameSlideOrder(nextThumbSlides, Array.from(thumbsSwiper.slides || []));

          const shouldFilterMain = !sameSlideOrder(nextMainSlides, productMedia.originalSlides);
          const shouldFilterThumbs =
            nextThumbSlides &&
            productMedia.originalThumbs &&
            !sameSlideOrder(nextThumbSlides, productMedia.originalThumbs);

          mainSwiper.allowTouchMove = false;
          if (isMainDifferent || isThumbDifferent) {
            setUpdating(true);
          }

          if (isMainDifferent) {
            filterSlidesSwiper(productMedia.originalSlides, mainSwiper, false);
          } else if (!shouldFilterMain) {
             // fallback to ensure it shows original if not filtering but different
             if (!sameSlideOrder(productMedia.originalSlides, Array.from(mainSwiper.slides || []))) {
                 filterSlidesSwiper(productMedia.originalSlides, mainSwiper, false);
             }
          }
          
          if (thumbsSwiper) {
            if (isThumbDifferent) {
              filterSlidesSwiper(productMedia.originalThumbs, thumbsSwiper, true);
            } else if (!shouldFilterThumbs && productMedia.originalThumbs) {
               if (!sameSlideOrder(productMedia.originalThumbs, Array.from(thumbsSwiper.slides || []))) {
                   filterSlidesSwiper(productMedia.originalThumbs, thumbsSwiper, true);
               }
            }
          } else {
            const thumbTrack = productMedia.querySelector('.thumbnail-carousel__track');
            if (thumbTrack && productMedia.originalThumbs) {
              const currentThumbSlides = Array.from(thumbTrack.querySelectorAll('.swiper-slide'));
              const targetThumbSlides = nextThumbSlides || productMedia.originalThumbs;
              if (!sameSlideOrder(targetThumbSlides, currentThumbSlides)) {
                thumbTrack.innerHTML = '';
                targetThumbSlides.forEach(slide => {
                  const clonedSlide = slide.cloneNode(true);
                  thumbTrack.appendChild(clonedSlide);
                });
              }
            }
          }

          try {
            if (
              mainSwiper.thumbs &&
              typeof mainSwiper.thumbs.update === 'function'
            ) {
              mainSwiper.thumbs.update(true);
            }
          } catch (e) {
            this.debugLog('thumbs_update_error', { message: e?.message });
          }

          const n = mainSwiper.slides?.length ?? 0;
          mainSwiper.allowTouchMove = n > 1;

        } else {
          const filterSlidesDom = (originalArray, wrapper) => {
            if (!originalArray || !wrapper) return;
            const variantSlides = buildVariantSlides(originalArray, false);
            wrapper.innerHTML = '';
            variantSlides.forEach(slide => wrapper.appendChild(slide));
          };
          const nextMainSlides = buildVariantSlides(productMedia.originalSlides, false);
          const shouldFilterMain = !sameSlideOrder(nextMainSlides, productMedia.originalSlides);
          if (shouldFilterMain) {
            filterSlidesDom(productMedia.originalSlides, swiperWrapper);
          }
          if (thumbWrapper && productMedia.originalThumbs) {
            const nextThumbSlides = buildVariantSlides(productMedia.originalThumbs, true);
            const shouldFilterThumbs = !sameSlideOrder(nextThumbSlides, productMedia.originalThumbs);
            if (shouldFilterThumbs) {
              thumbWrapper.innerHTML = '';
              nextThumbSlides.forEach(slide => thumbWrapper.appendChild(slide));
            }
          }

          const sliderEl = productMedia.querySelector('[data-slider]');
          const slideCount = swiperWrapper
            ? swiperWrapper.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
                .length
            : 0;
          sliderEl?.classList?.toggle('one-slide', slideCount <= 1);
        }

        if (typeof productMedia.updateGalleryNavigationVisibility === 'function') {
          productMedia.updateGalleryNavigationVisibility();
        }
      }

      // Wellness desktop update
      const wellnessThumbsWrapper = sectionElement.querySelector('.wellness-thumbs');
      if (wellnessThumbsWrapper) {
        if (!wellnessThumbsWrapper.originalThumbs) {
          wellnessThumbsWrapper.originalThumbs = Array.from(wellnessThumbsWrapper.querySelectorAll('.thumb'));
        }
        
        wellnessThumbsWrapper.innerHTML = '';
        
        let variantWellnessThumbs = [];
        if (wellnessGroupMediaIds && wellnessGroupMediaIds.size) {
          wellnessThumbsWrapper.originalThumbs.forEach(thumb => {
            const id = Number(thumb.getAttribute('data-media-id'));
            if (Number.isFinite(id) && wellnessGroupMediaIds.has(id)) {
              variantWellnessThumbs.push(thumb);
            }
          });
        }
        
        if (!variantWellnessThumbs.length) {
          const hasMatchingWellnessThumbs = wellnessThumbsWrapper.originalThumbs.some(thumb => {
            const slideColor = thumb.getAttribute('data-color');
            return slideColor && slideColor === selectedColor;
          });
          wellnessThumbsWrapper.originalThumbs.forEach(thumb => {
            const slideColor = thumb.getAttribute('data-color');
            if (!hasMatchingWellnessThumbs || slideColor === selectedColor) {
              variantWellnessThumbs.push(thumb);
            }
          });
        }
        
        if (!variantWellnessThumbs.length && wellnessThumbsWrapper.originalThumbs.length) {
          variantWellnessThumbs = wellnessThumbsWrapper.originalThumbs.slice();
        }
        
        variantWellnessThumbs.forEach(thumb => wellnessThumbsWrapper.appendChild(thumb));

        const firstThumb = wellnessThumbsWrapper.querySelector('.thumb');
        const mainImg = sectionElement.querySelector('#wellness-main-image');
        if (firstThumb && mainImg) {
          const firstThumbImg = firstThumb.querySelector('img');
          if (firstThumbImg && firstThumbImg.dataset.full) {
            mainImg.src = firstThumbImg.dataset.full;
          }
          wellnessThumbsWrapper.querySelectorAll('.thumb').forEach(t => t.classList.remove('is-active'));
          firstThumb.classList.add('is-active');
        }

      }
    }

    if (!variant.featured_media) {
      productMediaRoot?.classList.remove('is-media-updating');
      productMediaRoot?.classList.add('is-media-ready');
      productMediaRoot?.classList.add('is-color-ready');
      // #region debug-point B:carousel-flash-updating-off-missing-media
      try {
        this._dbgSend('H2', 'media_updating:off_missing_featured_media', {
          traceId: this._mediaUpdateTraceId || null,
          productMediaClassName: productMediaRoot?.className || null
        });
      } catch (e) {}
      // #endregion
      this.debugLog('media_missing', {
        variantId: variant.id
      });
      return;
    }

    const productMedia = productMediaRoot;
    if (!productMedia || !productMedia.setActiveMedia) {
      productMediaRoot?.classList.remove('is-media-updating');
      productMediaRoot?.classList.add('is-media-ready');
      productMediaRoot?.classList.add('is-color-ready');
      // #region debug-point B:carousel-flash-updating-off-missing-root
      try {
        this._dbgSend('H2', 'media_updating:off_missing_productMedia', {
          traceId: this._mediaUpdateTraceId || null,
          productMediaClassName: productMediaRoot?.className || null
        });
      } catch (e) {}
      // #endregion
      this.debugLog('productMedia_missing', {
        variantId: variant.id,
        featuredMediaId: variant.featured_media.id
      });
      return;
    }
    
    const runSetActive = () => {
      const refreshLayout = () => {
        try {
          if (typeof productMedia.refreshSwiperLayout === 'function') {
            productMedia.refreshSwiperLayout();
          }
        } catch (e) {}
      };

      refreshLayout();
      let targetMediaId = variant.featured_media.id;
      if (this._preserveActiveMediaOnNextUpdate && this._preserveActiveMediaId) {
        const preserveId = Number(this._preserveActiveMediaId);
        const canPreserve = Boolean(
          preserveId &&
            productMedia.querySelector(`[data-media-id="${preserveId}"]`)
        );
        if (canPreserve) {
          targetMediaId = preserveId;
        }
      }
      this._preserveActiveMediaOnNextUpdate = false;
      this._preserveActiveMediaId = null;
      productMedia.setActiveMedia(targetMediaId);
      refreshLayout();
      requestAnimationFrame(() => {
        refreshLayout();
        requestAnimationFrame(async () => {
          refreshLayout();
          this.debugMediaSnapshot('updateMedia:after-setActive');
          const waitForActiveImages = () => {
            const activeSlide =
              productMedia.querySelector('.swiper-slide-active') ||
              productMedia.querySelector('.swiper-slide');
            const imgs = activeSlide ? [...activeSlide.querySelectorAll('img')] : [];
            if (!imgs.length) return Promise.resolve();
            const decodeOrLoad = img => {
              if (img.complete && img.naturalWidth) return Promise.resolve();
              if (typeof img.decode === 'function') {
                return img.decode().catch(() => {});
              }
              return new Promise(resolve => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              });
            };
            const done = Promise.all(imgs.map(decodeOrLoad));
            const timeout = new Promise(resolve => setTimeout(resolve, 250));
            return Promise.race([done, timeout]);
          };

          await waitForActiveImages();
          refreshLayout();
          requestAnimationFrame(() => {
            productMedia.classList.remove('is-media-updating');
            productMedia.classList.add('is-media-ready');
            // is-media-ready is ALSO added independently, much
            // earlier, by product-media.js's own init -- unrelated
            // to whether the color/variant filtering above has
            // actually finished. is-color-ready is scoped narrowly
            // to just this moment (content filtered + images
            // decoded), so CSS can hide the gallery until content is
            // genuinely correct instead of just "the widget exists".
            productMedia.classList.add('is-color-ready');
            // #region debug-point B:carousel-flash-updating-off-ready
            try {
              this._dbgSend('H2', 'media_updating:off_ready', {
                traceId: this._mediaUpdateTraceId || null,
                productMediaClassName: productMedia.className || null
              });
            } catch (e) {}
            // #endregion
            this.debugMediaSnapshot('updateMedia:ready');
          });
          // #region debug-point D:updateMedia-end
          window.__traeDbgPdpBlipClicks?.('D', 'updateMedia:end', {
            traceId: this._mediaUpdateTraceId || null,
            tookMs: Date.now() - mediaUpdateStartedAt,
            activeMediaId: variant?.featured_media?.id || null
          });
          // #endregion
        });
      });
    };
    requestAnimationFrame(runSetActive);
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append'](
        'selling_plan',
        sellingPlanId
      );
    } else {
      params.delete('selling_plan');
    }
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  updateVariantInput() {
    const inputs = [
      this.querySelector('[name="id"]'),
      this.form?.querySelector?.('[name="id"]'),
      this.installmentsForm?.querySelector('[name="id"]')
    ];
    inputs.forEach(input => {
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form.submitButton;
    const price = this.querySelector(
      `#Product-Price-${this.dataset.sectionId}`
    );

    if (!submitButton) return;
    submitButton.textContent = window.theme.strings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  createDropdownFromButtons() {
    const inputWrappers = this.querySelectorAll(this.inputWrapper);

    inputWrappers.forEach((wrapper) => {
      const radios = wrapper.querySelectorAll('input[type="radio"]');
      const selectWrapper = wrapper.querySelector('[data-select-wrapper]');
      const select = wrapper.querySelector('[data-select]');

      if (!radios.length || !selectWrapper || !select) return;

      select.innerHTML = '';
      if (this.requireExplicitVariantSelection) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select';
        placeholder.selected = true;
        placeholder.disabled = true;
        select.appendChild(placeholder);
      }
      radios.forEach((radio) => {
        const option = document.createElement('option');
        option.value = radio.value;
        option.textContent = radio.value;
        if (radio.checked) option.selected = true;
        if (radio.disabled) option.disabled = true;
        select.appendChild(option);
      });

      select.addEventListener('change', (e) => {
        const selectedValue = e.target.value;

        // First uncheck all radios in this group
        radios.forEach(radio => {
          radio.checked = false;
          radio.closest('.product-options__item')?.classList.remove('is-active');
        });

        if (!selectedValue) {
          this.updateOptions();
          this.updateVariant();
          this.syncAddButtonState();
          this.updateMobileVariantStepperUI(false);
          this.updateVariantTabsInteractivity();
          return;
        }

        // Then check the selected one
        const targetRadio = Array.from(radios).find(r => r.value === selectedValue);
        if (targetRadio) {
          targetRadio.checked = true;
          targetRadio.closest('.product-options__item')?.classList.add('is-active');

          // Update the options state on the parent element
          this.updateOptions();

          // Dispatch the change event to trigger the variant change
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      selectWrapper.classList.remove('hidden');
    });
  }

  renderProductInfo() {
    if (!this.currentVariant) return;

    const currentVariantId = this.currentVariant.id;
    const requestId = ++this.renderRequestId;
    if (this.renderAbortController) {
      try {
        this.renderAbortController.abort();
      } catch (e) {}
    }
    this.renderAbortController =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;

    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      currentVariantId
    );
    params.append('section_id', this.dataset.sectionId);

    this.debugLog('render_start', { requestId, variantId: currentVariantId });

    fetch(
      `${this.dataset.url}?${params.toString()}`,
      this.renderAbortController
        ? { signal: this.renderAbortController.signal }
        : undefined
    )
      .then(response => response.text())
      .then(responseText => {
        if (requestId !== this.renderRequestId) {
          this.debugLog('render_stale', { requestId, variantId: currentVariantId });
          return;
        }
        if (!this.currentVariant || this.currentVariant.id !== currentVariantId) {
          this.debugLog('render_state_mismatch', {
            requestId,
            requestedVariantId: currentVariantId,
            currentVariantId: this.currentVariant?.id || null
          });
          return;
        }

        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        this.updateInventoryNotice(html);
        [
          `#Product-Price-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`
        ].map(liveRegionSelector => {
          const destination = document.querySelector(
            liveRegionSelector
          );
          const source = html.querySelector(liveRegionSelector);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
        const sourceSelector =
          html.querySelector(`#ProductSelector-${this.dataset.sectionId}`) ||
          html.querySelector('product-selector');
        const sourceStrapFieldset = sourceSelector?.querySelector(
          'fieldset[data-custom-strap-wrapper]'
        );
        const destinationStrapFieldset = this.querySelector(
          'fieldset[data-custom-strap-wrapper]'
        );
        if (sourceStrapFieldset) {
          if (destinationStrapFieldset) {
            const checkedValues = Array.from(
              destinationStrapFieldset.querySelectorAll(
                'input[data-custom-strap-input]:checked'
              )
            ).map(input => input.value);

            destinationStrapFieldset.innerHTML = sourceStrapFieldset.innerHTML;

            checkedValues.forEach(value => {
              const inputToRestore = destinationStrapFieldset.querySelector(
                `input[data-custom-strap-input][value="${CSS.escape(value)}"]`
              );
              if (inputToRestore) {
                inputToRestore.checked = true;
              }
            });
          } else {
            const clone = sourceStrapFieldset.cloneNode(true);
            const sourceNextElementSibling = sourceStrapFieldset.nextElementSibling;
            let inserted = false;

            if (sourceNextElementSibling && sourceNextElementSibling.id) {
              const targetNextElement = this.ownerDocument.getElementById(sourceNextElementSibling.id) || this.querySelector(`[id="${CSS.escape(sourceNextElementSibling.id)}"]`);
              if (targetNextElement && targetNextElement.parentNode) {
                targetNextElement.insertAdjacentElement('beforebegin', clone);
                inserted = true;
              }
            }

            if (!inserted) {
              const optionFieldsets = [
                ...this.querySelectorAll(
                  'fieldset.product-options[data-input-wrapper]:not([data-custom-strap-wrapper])'
                )
              ];
              let anchor = null;
              for (let i = 0; i < optionFieldsets.length; i++) {
                const legendTitle = optionFieldsets[i].querySelector('.product-selector__legend-title');
                if (legendTitle && legendTitle.textContent.toLowerCase().includes('size')) {
                  anchor = optionFieldsets[i];
                  break;
                }
              }
              if (anchor && anchor.parentNode) {
                anchor.insertAdjacentElement('beforebegin', clone);
              } else if (optionFieldsets.length > 0) {
                const lastAnchor = optionFieldsets[optionFieldsets.length - 1];
                lastAnchor.insertAdjacentElement('afterend', clone);
              } else {
                this.appendChild(clone);
              }
            }
          }
        } else if (destinationStrapFieldset) {
          destinationStrapFieldset.remove();
        }

        if (this.mobileVariantStepperActive) {
          const fieldsets = [
            ...this.querySelectorAll('fieldset.product-options[data-input-wrapper]')
          ];
          this.mobileVariantStepperFieldsets = fieldsets;
          this.mobileVariantStepperIndex = Math.min(
            this.mobileVariantStepperIndex || 0,
            Math.max(0, fieldsets.length - 1)
          );
          this.updateMobileVariantStepperUI(false);
        }

        this.updateOptions();
        const now = Date.now();
        const recentMediaUpdate =
          this._lastMediaUpdateVariantId === currentVariantId &&
          Number.isFinite(this._lastMediaUpdateAt) &&
          now - this._lastMediaUpdateAt < 5000;
        if (!recentMediaUpdate) {
          try {
            this.updateMedia();
          } catch (error) {
            this.debugLog('updateMedia_error', {
              message: error?.message,
              stack: error?.stack
            });
          }
        } else {
          // #region debug-point B:carousel-flash-skip
          try {
            this._dbgSend('H5', 'renderProductInfo:skip_updateMedia_recent', {
              requestId,
              variantId: currentVariantId,
              deltaMs: now - this._lastMediaUpdateAt
            });
          } catch (e) {}
          // #endregion
        }
        this.initCustomStrap();
        this.syncAddButtonState();

        this.debugLog('render_done', { requestId, variantId: currentVariantId });
      })
      .catch(error => {
        if (error?.name === 'AbortError') {
          this.debugLog('render_aborted', { requestId, variantId: currentVariantId });
          return;
        }
        this.debugLog('render_error', {
          requestId,
          variantId: currentVariantId,
          message: error?.message,
          stack: error?.stack
        });
      });
  }

  enableDropdownStyle() {
    const wrappers = Array.from(this.querySelectorAll('.js-product-options-value-list'));
    this.dropdownWrappers = wrappers;

    wrappers.forEach(wrapper => {
      if (wrapper.closest('.product-selector--buttons')) return;
      wrapper.dataset.open = 'false';

      const onClick = e => {
        const optionItem = e.target.closest('.product-options__item');
        if (optionItem) {
          return;
        }
        const isOpen = wrapper.dataset.open === 'true';
        wrapper.dataset.open = isOpen ? 'false' : 'true';
        e.stopPropagation();
      };

      const onChange = e => {
        if (e.target && e.target.matches && e.target.matches('input[type="radio"]')) {
          wrapper.dataset.open = 'false';
        }
      };

      this.dropdownListeners.set(wrapper, { onClick, onChange });
      wrapper.addEventListener('click', onClick);
      wrapper.addEventListener('change', onChange);
    });

    // Add global click event to close open dropdowns when clicking outside
    this.onWindowClickCloseDropdownBound = e => {
      wrappers.forEach(wrapper => {
        if (!wrapper.contains(e.target)) {
          wrapper.dataset.open = 'false';
        }
      });
    };
    window.addEventListener('click', this.onWindowClickCloseDropdownBound);
  }
}
customElements.define('product-selector', ProductSelector);
